import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  FileCheck2,
  Loader2,
  MapPinned,
  RefreshCcw,
  Sparkles,
  UserRound,
} from "lucide-react";
import CustomCaseFormFields, {
  type CustomCaseDevSuspectPresetOption,
  type CustomCaseFormSection,
} from "../components/custom/CustomCaseFormFields";
import CustomCaseNotifyMePanel from "../components/custom/CustomCaseNotifyMePanel";
import CustomOrderOwnerAccessGate from "../components/custom/CustomOrderOwnerAccessGate";
import { CustomOrderAPI } from "../lib/api";
import { cartService } from "../services/cartService";
import {
  clearPendingCustomOrderAccessToken,
  clearLastCustomOrderAccessToken,
  clearLastCustomOrderEmail,
  clearStoredCustomCaseFormData,
  CustomCaseFormData,
  CustomCaseFormErrors,
  CustomCaseSuspect,
  getIncompleteSuspectFields,
  getDefaultCustomCaseFormData,
  getEmptySuspect,
  isCustomCaseSuspectComplete,
  savePendingCustomOrderAccessToken,
  saveLastCustomOrderAccessToken,
  saveLastCustomOrderEmail,
  storeCustomCaseFormData,
  validateCustomCaseForm,
} from "../utils/customCase";
import {
  CASE_VIEW_PAGE_TITLE_CLASS,
  CASE_VIEW_PANEL_HEADING_CLASS,
  CASE_VIEW_PRIMARY_READING_CLASS,
  CASE_VIEW_SECTION_HEADING_CLASS,
  CASE_VIEW_SUPPORTING_READING_CLASS,
} from "../utils/caseViewTypography";
import {
  isFastSpringPopupPurchaseComplete,
  launchCustomCheckout,
  type FastSpringPopupClosePayload,
} from "../utils/customCheckout";
import {
  extractCustomLandingContextFromDraft,
  hasCustomLandingContext,
  normalizeCustomLandingContext,
  type CustomLandingLang,
} from "../utils/customLanding";
import {
  buildLocalizedCustomCaseFormCopy,
  localizeCustomCaseFormErrors,
} from "../utils/customCaseLocalization";
import { getLocalizedCustomCaseApiMessage } from "../utils/customCaseApiErrors";
import { devError, devLog, devWarn } from "../utils/devLog";

const INTAKE_STEPS: Array<{
  key: "details" | "suspects" | "review";
  label: string;
  description: string;
  sections?: CustomCaseFormSection[];
}> = [
  {
    key: "details",
    label: "Event Details",
    description:
      "Confirm your name, email, and where this case should take place.",
    sections: ["contact", "details"],
  },
  {
    key: "suspects",
    label: "Suspects",
    description:
      "Add the people, roles, and appearance details that make the case feel personal.",
    sections: ["suspects"],
  },
  {
    key: "review",
    label: "Review",
    description:
      "Check everything once before you submit the brief for generation.",
  },
];

const normalizeOrderDraftToFormData = (draft: any): CustomCaseFormData => ({
  ...getDefaultCustomCaseFormData(),
  ...(draft || {}),
  suspects: Array.isArray(draft?.suspects)
    ? draft.suspects.map((suspect: any) => ({
        ...getEmptySuspect(),
        ...suspect,
      }))
    : [],
});

type CustomOrderIdea = {
  idea_id: string;
  title: string;
  logline: string;
  summary: string;
  difficulty?: number;
  theme?: string;
  shellLabel?: string;
  shellSelectionReason?: string;
};

const CUSTOM_IDEA_BATCH_SIZE = 3;
const CUSTOM_IDEA_MAX_COUNT = 9;

const trimIdeaCopy = (value: unknown, maxLength = 220) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  const clipped = normalized.slice(0, maxLength).replace(/[,\s.:;!?-]+$/g, "");
  return `${clipped}...`;
};

const splitIntoSentences = (value: unknown) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(/(?<=[.?!])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

const buildIdeaChoiceBlurb = (idea: CustomOrderIdea) => {
  const loglineSentences = splitIntoSentences(idea.logline);
  const fitSentences = splitIntoSentences(idea.shellSelectionReason);
  const selectedSentences = [
    ...loglineSentences.slice(0, 2),
    ...(loglineSentences.length >= 2 ? [] : fitSentences.slice(0, 1)),
  ].filter(Boolean);

  return trimIdeaCopy(selectedSentences.join(" "), 210);
};

const normalizeGeneratedIdeas = (ideas: unknown): CustomOrderIdea[] =>
  (Array.isArray(ideas) ? ideas : []).map((idea: any) => ({
    idea_id: String(idea?.idea_id || "").trim(),
    title: String(idea?.title || "").trim(),
    logline: String(idea?.logline || "").trim(),
    summary: String(idea?.summary || "").trim(),
    difficulty: Number(idea?.difficulty || 3) || 3,
    theme: String(idea?.theme || "modern").trim() || "modern",
    shellLabel: String(idea?.shell_label || "").trim(),
    shellSelectionReason: String(idea?.shell_selection_reason || "").trim(),
  }));

const buildPresetSuspect = (
  suspect: Partial<CustomCaseSuspect> & Pick<CustomCaseSuspect, "fullName">,
): CustomCaseSuspect => ({
  ...getEmptySuspect(),
  ...suspect,
});

const DEV_SUSPECT_PRESETS: Record<string, CustomCaseSuspect[]> = {
  friends: [
    buildPresetSuspect({
      fullName: "Karen",
      sex: "F",
      age: "60",
      skinTone: "Light",
      height: "Short",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Wife of Glen, best friend of Gayle",
      occupation: "Retired Dentist",
    }),
    buildPresetSuspect({
      fullName: "Glen",
      sex: "M",
      age: "65",
      skinTone: "Light",
      height: "Average",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Husband of Karen",
      occupation: "Retired IT Professional",
    }),
    buildPresetSuspect({
      fullName: "Gayle",
      sex: "F",
      age: "60",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Long",
      hairColor: "Blonde",
      relationship: "Wife of Dave, best friend of Karen",
      occupation: "Retired Insurance adjuster",
    }),
    buildPresetSuspect({
      fullName: "Dave",
      sex: "M",
      age: "62",
      skinTone: "Light",
      height: "Average",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Husband of Gayle",
      occupation: "Retired IT Professional",
    }),
  ],
  family: [
    buildPresetSuspect({
      fullName: "Gayle",
      sex: "F",
      age: "60",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Long",
      hairColor: "Blonde",
      relationship: "Wife of Dave, mom of Connor",
      occupation: "Retired Insurance adjuster",
    }),
    buildPresetSuspect({
      fullName: "Dave",
      sex: "M",
      age: "62",
      skinTone: "Light",
      height: "Average",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Husband of Gayle, father of Connor",
      occupation: "Retired IT Professional",
    }),
    buildPresetSuspect({
      fullName: "Connor",
      sex: "M",
      age: "28",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Short",
      hairColor: "Blonde",
      relationship: "Son of Dave and Gayle, boyfriend of Talyor",
      occupation: "Senior IT Professional",
    }),
    buildPresetSuspect({
      fullName: "Talyor",
      sex: "F",
      age: "22",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Long",
      hairColor: "Brown",
      relationship: "Girlfriend of Connor",
      occupation: "University Student",
    }),
  ],
  coworkers: [
    buildPresetSuspect({
      fullName: "Dave",
      sex: "M",
      age: "62",
      skinTone: "Light",
      height: "Average",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Friends",
      occupation: "Retired IT Professional from Bank of Canada",
    }),
    buildPresetSuspect({
      fullName: "Gerrit",
      sex: "M",
      age: "55",
      skinTone: "Light",
      height: "Average",
      hairLength: "Short",
      hairColor: "Gray",
      relationship: "Friends",
      occupation: "IT Consultant Professional from Bank of Canada",
    }),
    buildPresetSuspect({
      fullName: "Sam",
      sex: "M",
      age: "32",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Short",
      hairColor: "Brown",
      relationship: "Friends",
      occupation: "IT Professional from Bank of Canada",
    }),
    buildPresetSuspect({
      fullName: "Melanie",
      sex: "F",
      age: "38",
      skinTone: "Light",
      height: "Tall",
      hairLength: "Long",
      hairColor: "Black",
      relationship: "Friends",
      occupation: "HR Professional from Bank of Canada",
    }),
  ],
};

const DEV_SUSPECT_PRESET_OPTIONS: CustomCaseDevSuspectPresetOption[] = [
  { key: "friends", label: "Friends", description: "Karen, Glen, Gayle, Dave" },
  {
    key: "family",
    label: "Family",
    description: "Gayle, Dave, Connor, Talyor",
  },
  {
    key: "coworkers",
    label: "Coworkers",
    description: "Dave, Gerrit, Sam, Melanie",
  },
];

const MISSING_SUSPECT_FIELD_LABELS: Record<
  CustomLandingLang,
  Partial<Record<keyof CustomCaseSuspect, string>>
> = {
  en: {
    fullName: "first name only",
    age: "age",
    sex: "sex",
    hairColor: "hair color",
    skinTone: "skin tone",
    occupation: "occupation",
    relationship: "relationship",
  },
  fr: {
    fullName: "prenom uniquement",
    age: "age",
    sex: "sexe",
    hairColor: "couleur des cheveux",
    skinTone: "teinte de peau",
    occupation: "profession",
    relationship: "relation",
  },
  es: {
    fullName: "solo nombre",
    age: "edad",
    sex: "sexo",
    hairColor: "color de pelo",
    skinTone: "tono de piel",
    occupation: "profesion",
    relationship: "relacion",
  },
  de: {
    fullName: "nur vorname",
    age: "alter",
    sex: "geschlecht",
    hairColor: "haarfarbe",
    skinTone: "hautton",
    occupation: "beruf",
    relationship: "beziehung",
  },
  it: {
    fullName: "solo nome",
    age: "eta",
    sex: "sesso",
    hairColor: "colore dei capelli",
    skinTone: "tono della pelle",
    occupation: "professione",
    relationship: "relazione",
  },
};

const formatMissingSuspectFields = (
  fields: Array<keyof CustomCaseSuspect>,
  lang: CustomLandingLang,
) =>
  fields.map((field) => MISSING_SUSPECT_FIELD_LABELS[lang]?.[field] || field);

const INTAKE_PAGE_COPY = {
  en: {
    helmetTitle: "Custom Case Intake | Cold Case Game Kits",
    helmetDescription:
      "Finish your paid custom case intake and send us the suspect details we need to build your personalized mystery.",
    eyebrow: "Custom Case Intake Form",
    title: "Enter Information Below to Complete Your Case",
    ownerAccessTitle: "Verify Your Intake Access",
    ownerAccessDescription:
      "This private intake is tied to the order email. We will send a one-time code before opening or updating the brief from this browser.",
    ownerAccessCopy: {
      loading: "Checking private access...",
      badge: "Private Owner Access",
      sendCodeToOrderEmail: "Send the code to",
      rememberedNotice:
        "We only ask for this on a new browser or device. After verification, this browser stays remembered for future visits.",
      requestIdle: "Email Access Code",
      requestBusy: "Sending Code...",
      codeLabel: "6-digit code",
      codePlaceholder: "123456",
      verifyIdle: "Verify And Continue",
      verifyBusy: "Verifying...",
      clearRememberedAccess: "Clear Remembered Access",
      devCodePreview: "Dev code preview",
      invalidCode: "Enter the 6-digit code from your email.",
      statusCodeSent: "Access code sent. Enter it below to continue.",
      statusVerified: "Access confirmed for this browser.",
      statusCleared: "Remembered access cleared for this browser.",
    },
    showOrderDetails: "Show Order Details",
    hideOrderDetails: "Hide Order Details",
    orderStatusLabel: "Order Status",
    paymentLabel: "Payment",
    accessTokenLabel: "Access Token",
    paymentConfirmedLabel: "Confirmed",
    paymentPendingLabel: "Pending",
    checkPaymentAgain: "Check Payment Again",
    reopenCheckout: "Reopen Checkout",
    bypassPaymentInDev: "Bypass Payment In Dev",
    confirmingDevPayment: "Confirming Dev Payment...",
    backToCustomCases: "Back to Custom Cases",
    loadingOrder: "Loading your custom order...",
    paymentBanner:
      "Payment confirmed. Save a draft anytime, then submit for build when the case information looks right.",
    stepWord: "Step",
    steps: {
      details: {
        label: "Event Details",
        description:
          "Confirm your name, email, and where this case should take place.",
      },
      suspects: {
        label: "Suspects",
        description:
          "Add the people, roles, and appearance details that make the case feel personal.",
      },
      review: {
        label: "Review",
        description:
          "Check everything once before you submit the brief for generation.",
      },
    },
    reviewSummary: "Order Summary",
    suspectReview: "Suspect Review",
    firstName: "First Name",
    email: "Email",
    city: "City",
    location: "Location",
    referralSource: "Referral Source",
    suspectsCount: "Suspects",
    ageSex: "Age / Sex",
    hairSkin: "Hair / Skin",
    occupation: "Occupation",
    relationship: "Relationship",
    notes: "Notes",
    notProvided: "Not provided",
    notSpecified: "Not specified",
    noExtraNotes: "No extra notes",
    continueButton: "Continue",
    backButton: "Back",
    saveDraftButton: "Save Draft",
    savingButton: "Saving...",
    submittingButton: "Submitting...",
    submitButton: "Info complete, please create the custom case.",
    generatingIdeasTitle: "Generating your 3 case directions",
    generatingIdeasBody:
      "We are using your city, named suspects, and relationship details to build tailored story options.",
    ideasEyebrow: "Choose The Case Direction",
    ideasTitle: "Pick the story you want us to build",
    ideasBody:
      "These case directions come from different mystery frameworks chosen around your city, suspect list, and relationship details. Pick one and we will queue the full custom case generation from there.",
    optionLabel: "Option",
    selectedLabel: "Selected",
    getMoreIdeas: "Get 3 New Ideas",
    getMoreIdeasBusy: "Generating 3 More Ideas...",
    getMoreIdeasRemaining: "{count} left",
    queuedLabel: "Queued",
    difficultyLabel: "Difficulty",
    modernThemeLabel: "Modern",
    classicThemeLabel: "Classic",
    noirThemeLabel: "Noir",
    buildThisCase: "Build This Case",
    queueing: "Queueing...",
    queuedForBuild: "Queued For Build",
    selectedIdeaSuffix: "is selected.",
    paymentLocked:
      "Payment has not been confirmed yet, so the intake form is still locked. If you already completed checkout, use Check Payment Again. If not, reopen checkout and finish the purchase first.",
    devShortcutTitle: "Local testing shortcut",
    devShortcutBody:
      "Because you are in dev, you can use Bypass Payment In Dev above to unlock the intake without going through FastSpring.",
    statusSummaryPending:
      "We have not confirmed your FastSpring payment yet. If you just finished checkout, wait a moment and check again.",
    statusSummaryReady:
      "Payment confirmed. You can finish and submit your custom-case details below.",
    statusSummaryIdeasGenerating:
      "Payment confirmed. We are turning your brief into three tailored case directions now.",
    statusSummaryChooseIdea:
      "Your intake is in. Pick the case direction you want us to build and we will queue the custom case from there.",
    statusSummaryQueued:
      "Your selected concept is now in the build pipeline. We will keep this page updated automatically as the case moves forward.",
    statusSummaryReviewRequired:
      "Your case build finished, but it is now in quality review because we caught issues that need to be fixed before release.",
    statusSummaryFailed:
      "The last build run stopped before completion. Use Start fresh on this order to queue a clean rebuild.",
    statusSummaryComplete:
      "Your selected concept has finished building and this order is now marked complete.",
    statusDraftSaved: "Draft saved. You can come back and finish later.",
    statusValidationMissing:
      "A few required fields are still missing. We highlighted them so you can finish the intake quickly.",
    statusSubmitted:
      "Your intake is in. Choose the case direction you want us to build next.",
    statusIdeaQueued:
      "Your custom case is queued for generation. We will build this concept into a full case file.",
    statusIdeaAccepted:
      "Your concept is selected. Generation is accepted and ready for the next workflow step.",
    statusMoreIdeas:
      "We added three more case directions. You now have {count} ideas to compare before choosing one.",
    statusFinalIdeas:
      "We added the final three case directions. You now have the full {count} ideas to compare before choosing one.",
    statusDevBypass:
      "Dev payment bypass applied. You can finish and submit the custom-case intake now.",
    orderStatusLoading: "Loading",
    finishSuspectBeforeAdding:
      "Finish Suspect {index} before adding another one.",
    finishSuspectBeforeAddingWithMissing:
      "Finish Suspect {index} before adding another one. Missing: {fields}.",
    devPresetLoaded:
      "Loaded the {preset} dev suspect preset for quick intake testing.",
    statusCheckoutRefreshAfterPayment:
      "Payment window closed after checkout. Refreshing your order...",
    statusCheckoutClosedBeforePayment:
      "Checkout closed before payment was completed. Reopen checkout when you're ready.",
    checkoutUnavailable: "FastSpring checkout is not ready right now.",
    statusRestartingBuild: "Restarting your custom case build now...",
    statusBuildRestartQueued:
      "Your custom case build has been restarted and requeued.",
    statusBuildRestartAccepted:
      "Your custom case build restart was accepted and is waiting for the next workflow step.",
    generationRunAttached: "Generation run {runId} is attached to this order.",
  },
  fr: {
    helmetTitle: "Brief d'affaire personnalisee | Cold Case Game Kits",
    helmetDescription:
      "Terminez votre brief prive et envoyez les details dont nous avons besoin pour construire votre mystere personnalise.",
    eyebrow: "Formulaire de brief personnalise",
    title: "Entrez les informations pour finaliser votre affaire",
    ownerAccessTitle: "Verifiez l'acces a votre brief",
    ownerAccessDescription:
      "Ce brief prive est lie a l'email de la commande. Nous enverrons un code a usage unique avant d'ouvrir ou mettre a jour ce dossier depuis ce navigateur.",
    ownerAccessCopy: {
      loading: "Verification de l'acces prive...",
      badge: "Acces proprietaire prive",
      sendCodeToOrderEmail: "Envoyer le code a",
      rememberedNotice:
        "Nous ne le demandons que sur un nouveau navigateur ou appareil. Une fois verifie, ce navigateur reste memorise pour les prochaines visites.",
      requestIdle: "Envoyer le code d'acces",
      requestBusy: "Envoi du code...",
      codeLabel: "Code a 6 chiffres",
      verifyIdle: "Verifier et continuer",
      verifyBusy: "Verification...",
      clearRememberedAccess: "Effacer l'acces memorise",
      devCodePreview: "Code dev",
      invalidCode: "Entrez le code a 6 chiffres recu par email.",
      statusCodeSent: "Code envoye. Entrez-le ci-dessous pour continuer.",
      statusVerified: "Acces confirme pour ce navigateur.",
      statusCleared: "Acces memorise efface pour ce navigateur.",
    },
    showOrderDetails: "Voir les details de la commande",
    hideOrderDetails: "Masquer les details de la commande",
    orderStatusLabel: "Statut de la commande",
    paymentLabel: "Paiement",
    accessTokenLabel: "Jeton d'acces",
    paymentConfirmedLabel: "Confirme",
    paymentPendingLabel: "En attente",
    checkPaymentAgain: "Verifier le paiement",
    reopenCheckout: "Rouvrir le paiement",
    bypassPaymentInDev: "Ignorer le paiement en dev",
    confirmingDevPayment: "Confirmation du paiement dev...",
    backToCustomCases: "Retour aux affaires personnalisees",
    loadingOrder: "Chargement de votre commande personnalisee...",
    paymentBanner:
      "Paiement confirme. Enregistrez un brouillon a tout moment puis envoyez-le quand tout vous semble correct.",
    stepWord: "Etape",
    steps: {
      details: {
        label: "Details de l'evenement",
        description:
          "Confirmez votre nom, votre email et le lieu ou l'affaire doit se derouler.",
      },
      suspects: {
        label: "Suspects",
        description:
          "Ajoutez les personnes, les roles et les details d'apparence qui rendent l'affaire personnelle.",
      },
      review: {
        label: "Verification",
        description: "Relisez tout avant d'envoyer le brief pour generation.",
      },
    },
    reviewSummary: "Resume de la commande",
    suspectReview: "Verification des suspects",
    firstName: "Prenom",
    email: "Email",
    city: "Ville",
    location: "Lieu",
    referralSource: "Source",
    suspectsCount: "Suspects",
    ageSex: "Age / Sexe",
    hairSkin: "Cheveux / Peau",
    occupation: "Profession",
    relationship: "Relation",
    notes: "Notes",
    notProvided: "Non renseigne",
    notSpecified: "Non precise",
    noExtraNotes: "Aucune note supplementaire",
    continueButton: "Continuer",
    backButton: "Retour",
    saveDraftButton: "Enregistrer le brouillon",
    savingButton: "Enregistrement...",
    submittingButton: "Envoi...",
    submitButton: "Les informations sont completes, creez l'affaire.",
    generatingIdeasTitle: "Generation de vos 3 directions d'affaire",
    generatingIdeasBody:
      "Nous utilisons votre ville, les suspects nommes et leurs relations pour creer des options d'histoire adaptees.",
    ideasEyebrow: "Choisissez la direction",
    ideasTitle: "Choisissez l'histoire a construire",
    ideasBody:
      "Ces directions viennent de differents cadres de mystere choisis selon votre ville, vos suspects et leurs relations. Choisissez-en une et nous lancerons la generation complete.",
    optionLabel: "Option",
    selectedLabel: "Selectionnee",
    getMoreIdeas: "Obtenir 3 nouvelles idees",
    getMoreIdeasBusy: "Generation de 3 nouvelles idees...",
    getMoreIdeasRemaining: "{count} restantes",
    queuedLabel: "En file",
    difficultyLabel: "Difficulte",
    modernThemeLabel: "Moderne",
    classicThemeLabel: "Classique",
    noirThemeLabel: "Noir",
    buildThisCase: "Construire cette affaire",
    queueing: "Mise en file...",
    queuedForBuild: "En file de generation",
    selectedIdeaSuffix: "est selectionnee.",
    paymentLocked:
      "Le paiement n'a pas encore ete confirme, donc le brief reste verrouille. Si vous avez deja termine le paiement, utilisez Verifier le paiement. Sinon, rouvrez le paiement et terminez l'achat.",
    devShortcutTitle: "Raccourci de test local",
    devShortcutBody:
      "Comme vous etes en dev, vous pouvez utiliser Ignorer le paiement en dev ci-dessus pour debloquer le brief sans passer par FastSpring.",
    statusSummaryPending:
      "Nous n'avons pas encore confirme votre paiement FastSpring. Si vous venez de terminer, attendez un instant puis reverifiez.",
    statusSummaryReady:
      "Paiement confirme. Vous pouvez maintenant terminer et envoyer les details de votre affaire.",
    statusSummaryIdeasGenerating:
      "Paiement confirme. Nous transformons votre brief en trois directions d'affaire adaptees.",
    statusSummaryChooseIdea:
      "Votre brief est recu. Choisissez la direction que vous voulez nous faire construire.",
    statusSummaryQueued:
      "La direction choisie est maintenant dans la file de generation. Cette page se mettra a jour automatiquement.",
    statusSummaryReviewRequired:
      "La generation de votre affaire est terminee, mais elle est maintenant en controle qualite car nous avons releve des points a corriger avant la livraison.",
    statusSummaryFailed:
      "La derniere generation s'est arretee avant la fin. Utilisez Recommencer pour lancer une nouvelle generation.",
    statusSummaryComplete:
      "La direction choisie a termine sa construction et cette commande est maintenant complete.",
    statusDraftSaved:
      "Brouillon enregistre. Vous pouvez revenir plus tard pour terminer.",
    statusValidationMissing:
      "Quelques champs obligatoires manquent encore. Nous les avons surlignes pour vous aider a finir rapidement.",
    statusSubmitted:
      "Votre brief est envoye. Choisissez maintenant la direction a construire.",
    statusIdeaQueued:
      "Votre affaire personnalisee est en file de generation. Nous allons construire ce concept en dossier complet.",
    statusIdeaAccepted:
      "Votre concept est selectionne. La generation est acceptee et attend l'etape suivante.",
    statusMoreIdeas:
      "Nous avons ajoute trois nouvelles directions. Vous avez maintenant {count} idees a comparer avant de choisir.",
    statusFinalIdeas:
      "Nous avons ajoute les trois dernieres directions. Vous avez maintenant les {count} idees completes a comparer avant de choisir.",
    statusDevBypass:
      "Bypass dev applique. Vous pouvez maintenant terminer et envoyer le brief.",
    orderStatusLoading: "Chargement",
    finishSuspectBeforeAdding:
      "Terminez le suspect {index} avant d'en ajouter un autre.",
    finishSuspectBeforeAddingWithMissing:
      "Terminez le suspect {index} avant d'en ajouter un autre. Il manque : {fields}.",
    devPresetLoaded:
      "Le preset dev {preset} a ete charge pour tester rapidement le brief.",
    statusCheckoutRefreshAfterPayment:
      "La fenetre de paiement s'est fermee apres le checkout. Actualisation de votre commande...",
    statusCheckoutClosedBeforePayment:
      "Le checkout s'est ferme avant la confirmation du paiement. Rouvrez le paiement quand vous etes pret.",
    checkoutUnavailable:
      "FastSpring n'est pas pret pour le paiement pour le moment.",
    statusRestartingBuild:
      "Redemarrage de la generation de votre affaire personnalisee...",
    statusBuildRestartQueued:
      "La generation de votre affaire a ete relancee et remise dans la file.",
    statusBuildRestartAccepted:
      "Le redemarrage de votre affaire a ete accepte et attend l'etape suivante du workflow.",
    generationRunAttached:
      "La generation {runId} est associee a cette commande.",
  },
  es: {
    helmetTitle: "Formulario del caso personalizado | Cold Case Game Kits",
    helmetDescription:
      "Termina tu formulario privado y envianos los detalles necesarios para construir tu misterio personalizado.",
    eyebrow: "Formulario de caso personalizado",
    title: "Introduce la informacion para completar tu caso",
    ownerAccessTitle: "Verifica el acceso a tu formulario",
    ownerAccessDescription:
      "Este formulario privado esta vinculado al correo del pedido. Enviaremos un codigo de un solo uso antes de abrir o actualizar el caso en este navegador.",
    ownerAccessCopy: {
      loading: "Comprobando acceso privado...",
      badge: "Acceso privado del propietario",
      sendCodeToOrderEmail: "Enviar el codigo a",
      rememberedNotice:
        "Solo lo pedimos en un navegador o dispositivo nuevo. Tras verificarlo, este navegador quedara recordado para futuras visitas.",
      requestIdle: "Enviar codigo de acceso",
      requestBusy: "Enviando codigo...",
      codeLabel: "Codigo de 6 digitos",
      verifyIdle: "Verificar y continuar",
      verifyBusy: "Verificando...",
      clearRememberedAccess: "Borrar acceso recordado",
      devCodePreview: "Codigo dev",
      invalidCode: "Introduce el codigo de 6 digitos que recibiste por correo.",
      statusCodeSent: "Codigo enviado. Introducelo abajo para continuar.",
      statusVerified: "Acceso confirmado para este navegador.",
      statusCleared: "Acceso recordado eliminado para este navegador.",
    },
    showOrderDetails: "Ver detalles del pedido",
    hideOrderDetails: "Ocultar detalles del pedido",
    orderStatusLabel: "Estado del pedido",
    paymentLabel: "Pago",
    accessTokenLabel: "Token de acceso",
    paymentConfirmedLabel: "Confirmado",
    paymentPendingLabel: "Pendiente",
    checkPaymentAgain: "Comprobar pago otra vez",
    reopenCheckout: "Reabrir pago",
    bypassPaymentInDev: "Omitir pago en dev",
    confirmingDevPayment: "Confirmando pago dev...",
    backToCustomCases: "Volver a los casos personalizados",
    loadingOrder: "Cargando tu pedido personalizado...",
    paymentBanner:
      "Pago confirmado. Guarda un borrador cuando quieras y envialo cuando todo este correcto.",
    stepWord: "Paso",
    steps: {
      details: {
        label: "Detalles del evento",
        description:
          "Confirma tu nombre, tu correo y donde debe ocurrir el caso.",
      },
      suspects: {
        label: "Sospechosos",
        description:
          "Anade las personas, roles y detalles fisicos que hacen que el caso se sienta personal.",
      },
      review: {
        label: "Revision",
        description:
          "Revisa todo antes de enviar el formulario para la generacion.",
      },
    },
    reviewSummary: "Resumen del pedido",
    suspectReview: "Revision de sospechosos",
    firstName: "Nombre",
    email: "Correo",
    city: "Ciudad",
    location: "Lugar",
    referralSource: "Origen",
    suspectsCount: "Sospechosos",
    ageSex: "Edad / Sexo",
    hairSkin: "Pelo / Piel",
    occupation: "Profesion",
    relationship: "Relacion",
    notes: "Notas",
    notProvided: "Sin indicar",
    notSpecified: "Sin especificar",
    noExtraNotes: "Sin notas extra",
    continueButton: "Continuar",
    backButton: "Atras",
    saveDraftButton: "Guardar borrador",
    savingButton: "Guardando...",
    submittingButton: "Enviando...",
    submitButton: "La informacion esta lista, crea el caso personalizado.",
    generatingIdeasTitle: "Generando tus 3 direcciones de caso",
    generatingIdeasBody:
      "Estamos usando tu ciudad, los sospechosos y sus relaciones para crear opciones de historia adaptadas.",
    ideasEyebrow: "Elige la direccion del caso",
    ideasTitle: "Elige la historia que quieres construir",
    ideasBody:
      "Estas direcciones parten de distintos marcos de misterio elegidos segun tu ciudad, tus sospechosos y sus relaciones. Elige una y pondremos en cola la generacion completa.",
    optionLabel: "Opcion",
    selectedLabel: "Seleccionada",
    getMoreIdeas: "Conseguir 3 ideas nuevas",
    getMoreIdeasBusy: "Generando 3 ideas nuevas...",
    getMoreIdeasRemaining: "{count} restantes",
    queuedLabel: "En cola",
    difficultyLabel: "Dificultad",
    modernThemeLabel: "Moderno",
    classicThemeLabel: "Clasico",
    noirThemeLabel: "Noir",
    buildThisCase: "Construir este caso",
    queueing: "Poniendo en cola...",
    queuedForBuild: "En cola de generacion",
    selectedIdeaSuffix: "esta seleccionada.",
    paymentLocked:
      "El pago todavia no se ha confirmado, asi que el formulario sigue bloqueado. Si ya terminaste el pago, usa Comprobar pago otra vez. Si no, reabre el pago y termina la compra.",
    devShortcutTitle: "Atajo para pruebas locales",
    devShortcutBody:
      "Como estas en dev, puedes usar Omitir pago en dev para desbloquear el formulario sin pasar por FastSpring.",
    statusSummaryPending:
      "Todavia no hemos confirmado tu pago de FastSpring. Si acabas de terminarlo, espera un momento y vuelve a comprobarlo.",
    statusSummaryReady:
      "Pago confirmado. Ya puedes terminar y enviar los detalles de tu caso personalizado.",
    statusSummaryIdeasGenerating:
      "Pago confirmado. Estamos convirtiendo tu formulario en tres direcciones de caso adaptadas.",
    statusSummaryChooseIdea:
      "Tu formulario ya esta enviado. Elige la direccion que quieres que construyamos.",
    statusSummaryQueued:
      "La direccion elegida ya esta en la cola de generacion. Esta pagina se actualizara automaticamente.",
    statusSummaryReviewRequired:
      "La construccion de su caso termino, pero ahora esta en revision de calidad porque detectamos detalles que debemos corregir antes de liberarlo.",
    statusSummaryFailed:
      "La ultima generacion se detuvo antes de completarse. Usa Empezar de nuevo para lanzar una nueva ejecucion.",
    statusSummaryComplete:
      "La direccion elegida ya se ha construido y este pedido ahora esta completo.",
    statusDraftSaved:
      "Borrador guardado. Puedes volver mas tarde para terminarlo.",
    statusValidationMissing:
      "Todavia faltan algunos campos obligatorios. Los hemos resaltado para que termines rapido.",
    statusSubmitted:
      "Tu formulario esta enviado. Ahora elige la direccion que quieres construir.",
    statusIdeaQueued:
      "Tu caso personalizado esta en cola de generacion. Vamos a convertir este concepto en un expediente completo.",
    statusIdeaAccepted:
      "Tu concepto esta seleccionado. La generacion esta aceptada y lista para el siguiente paso.",
    statusMoreIdeas:
      "Hemos anadido tres direcciones mas. Ahora tienes {count} ideas para comparar antes de elegir.",
    statusFinalIdeas:
      "Hemos anadido las tres ultimas direcciones. Ahora tienes las {count} ideas completas para comparar antes de elegir.",
    statusDevBypass:
      "Omitido en dev. Ya puedes terminar y enviar el formulario.",
    orderStatusLoading: "Cargando",
    finishSuspectBeforeAdding:
      "Termina el sospechoso {index} antes de anadir otro.",
    finishSuspectBeforeAddingWithMissing:
      "Termina el sospechoso {index} antes de anadir otro. Falta: {fields}.",
    devPresetLoaded:
      "Se ha cargado el preset dev {preset} para probar el formulario mas rapido.",
    statusCheckoutRefreshAfterPayment:
      "La ventana de pago se cerro despues del checkout. Actualizando tu pedido...",
    statusCheckoutClosedBeforePayment:
      "El checkout se cerro antes de que se completara el pago. Reabre el pago cuando quieras.",
    checkoutUnavailable:
      "FastSpring no esta listo para el pago en este momento.",
    statusRestartingBuild:
      "Reiniciando ahora la generacion de tu caso personalizado...",
    statusBuildRestartQueued:
      "La generacion de tu caso se ha reiniciado y vuelto a poner en cola.",
    statusBuildRestartAccepted:
      "El reinicio de tu caso fue aceptado y esta esperando el siguiente paso del flujo.",
    generationRunAttached: "La ejecucion {runId} esta asociada a este pedido.",
  },
  de: {
    helmetTitle: "Intake fur den individuellen Fall | Cold Case Game Kits",
    helmetDescription:
      "Schliesse deinen privaten Intake ab und sende uns die Details, die wir fur deinen personlichen Fall brauchen.",
    eyebrow: "Intake fur individuellen Fall",
    title: "Gib die Informationen ein, um deinen Fall abzuschliessen",
    ownerAccessTitle: "Prufe den Zugriff auf deinen Intake",
    ownerAccessDescription:
      "Dieser private Intake ist an die Bestell-E-Mail gebunden. Wir senden einen Einmalcode, bevor wir den Fall in diesem Browser offnen oder aktualisieren.",
    ownerAccessCopy: {
      loading: "Privater Zugriff wird gepruft...",
      badge: "Privater Eigentumerzugang",
      sendCodeToOrderEmail: "Code senden an",
      rememberedNotice:
        "Wir fragen nur auf einem neuen Browser oder Gerat danach. Nach der Verifizierung bleibt dieser Browser fur spater gemerkt.",
      requestIdle: "Zugangscode per E-Mail senden",
      requestBusy: "Code wird gesendet...",
      codeLabel: "6-stelliger Code",
      verifyIdle: "Prufen und weiter",
      verifyBusy: "Wird gepruft...",
      clearRememberedAccess: "Gemerkten Zugriff loschen",
      devCodePreview: "Dev-Code",
      invalidCode: "Gib den 6-stelligen Code aus deiner E-Mail ein.",
      statusCodeSent: "Code gesendet. Gib ihn unten ein, um fortzufahren.",
      statusVerified: "Zugriff fur diesen Browser bestaetigt.",
      statusCleared: "Gemerkter Zugriff fur diesen Browser entfernt.",
    },
    showOrderDetails: "Bestelldetails anzeigen",
    hideOrderDetails: "Bestelldetails ausblenden",
    orderStatusLabel: "Bestellstatus",
    paymentLabel: "Zahlung",
    accessTokenLabel: "Access-Token",
    paymentConfirmedLabel: "Bestatigt",
    paymentPendingLabel: "Ausstehend",
    checkPaymentAgain: "Zahlung erneut prufen",
    reopenCheckout: "Checkout erneut offnen",
    bypassPaymentInDev: "Zahlung in Dev uberspringen",
    confirmingDevPayment: "Dev-Zahlung wird bestatigt...",
    backToCustomCases: "Zuruck zu individuellen Fallen",
    loadingOrder: "Deine individuelle Bestellung wird geladen...",
    paymentBanner:
      "Zahlung bestatigt. Speichere jederzeit einen Entwurf und sende ihn ab, wenn alles richtig aussieht.",
    stepWord: "Schritt",
    steps: {
      details: {
        label: "Event-Details",
        description:
          "Bestatige deinen Namen, deine E-Mail und den Ort des Falls.",
      },
      suspects: {
        label: "Verdachtige",
        description:
          "Fuge die Personen, Rollen und Aussehensdetails hinzu, die den Fall personlich machen.",
      },
      review: {
        label: "Prufung",
        description:
          "Prufe alles noch einmal, bevor du den Intake fur die Generierung absendest.",
      },
    },
    reviewSummary: "Bestellubersicht",
    suspectReview: "Verdachtigenprufung",
    firstName: "Vorname",
    email: "E-Mail",
    city: "Stadt",
    location: "Ort",
    referralSource: "Quelle",
    suspectsCount: "Verdachtige",
    ageSex: "Alter / Geschlecht",
    hairSkin: "Haare / Haut",
    occupation: "Beruf",
    relationship: "Beziehung",
    notes: "Notizen",
    notProvided: "Nicht angegeben",
    notSpecified: "Nicht spezifiziert",
    noExtraNotes: "Keine zusatzlichen Notizen",
    continueButton: "Weiter",
    backButton: "Zuruck",
    saveDraftButton: "Entwurf speichern",
    savingButton: "Wird gespeichert...",
    submittingButton: "Wird gesendet...",
    submitButton:
      "Die Angaben sind vollstandig, bitte erstelle den individuellen Fall.",
    generatingIdeasTitle: "Deine 3 Falldirectionen werden erstellt",
    generatingIdeasBody:
      "Wir nutzen deine Stadt, die benannten Verdachtigen und ihre Beziehungen, um passende Story-Optionen zu erstellen.",
    ideasEyebrow: "Wahle die Falldirection",
    ideasTitle: "Wahle die Geschichte, die wir bauen sollen",
    ideasBody:
      "Diese Richtungen stammen aus verschiedenen Mystery-Rahmen, die nach Stadt, Verdachtigen und Beziehungen ausgewahlt wurden. Wale eine aus und wir starten die komplette Generierung.",
    optionLabel: "Option",
    selectedLabel: "Ausgewahlt",
    getMoreIdeas: "3 neue Ideen holen",
    getMoreIdeasBusy: "3 neue Ideen werden erstellt...",
    getMoreIdeasRemaining: "{count} ubrig",
    queuedLabel: "In Warteschlange",
    difficultyLabel: "Schwierigkeit",
    modernThemeLabel: "Modern",
    classicThemeLabel: "Klassisch",
    noirThemeLabel: "Noir",
    buildThisCase: "Diesen Fall bauen",
    queueing: "Wird eingereiht...",
    queuedForBuild: "Zur Generierung eingereiht",
    selectedIdeaSuffix: "ist ausgewahlt.",
    paymentLocked:
      "Die Zahlung wurde noch nicht bestatigt, daher bleibt der Intake gesperrt. Wenn du den Checkout bereits abgeschlossen hast, nutze Zahlung erneut prufen. Wenn nicht, offne den Checkout erneut und schliesse den Kauf ab.",
    devShortcutTitle: "Lokaler Test-Kurzbefehl",
    devShortcutBody:
      "Da du in Dev bist, kannst du oben Zahlung in Dev uberspringen verwenden, um den Intake ohne FastSpring freizuschalten.",
    statusSummaryPending:
      "Wir haben deine FastSpring-Zahlung noch nicht bestatigt. Wenn du gerade bezahlt hast, warte einen Moment und pruefe dann erneut.",
    statusSummaryReady:
      "Zahlung bestatigt. Du kannst jetzt die Details deines individuellen Falls abschliessen und absenden.",
    statusSummaryIdeasGenerating:
      "Zahlung bestatigt. Wir wandeln deinen Intake jetzt in drei passende Falldirectionen um.",
    statusSummaryChooseIdea:
      "Dein Intake ist eingegangen. Wale jetzt die Richtung, die wir bauen sollen.",
    statusSummaryQueued:
      "Das gewahlte Konzept befindet sich jetzt in der Generierungspipeline. Diese Seite aktualisiert sich automatisch.",
    statusSummaryReviewRequired:
      "Der Build Ihres Falls ist abgeschlossen, befindet sich jetzt aber in der Qualitatsprufung, weil wir vor der Freigabe noch Punkte korrigieren mussen.",
    statusSummaryFailed:
      "Der letzte Build-Lauf wurde vor Abschluss gestoppt. Nutze Neu starten, um einen neuen Lauf zu starten.",
    statusSummaryComplete:
      "Das gewahlte Konzept ist fertig gebaut und diese Bestellung ist jetzt abgeschlossen.",
    statusDraftSaved:
      "Entwurf gespeichert. Du kannst spater zuruckkommen und weitermachen.",
    statusValidationMissing:
      "Einige Pflichtfelder fehlen noch. Wir haben sie markiert, damit du den Intake schnell abschliessen kannst.",
    statusSubmitted:
      "Dein Intake ist abgesendet. Wale jetzt die Richtung, die wir bauen sollen.",
    statusIdeaQueued:
      "Dein individueller Fall wurde zur Generierung eingereiht. Wir bauen dieses Konzept jetzt zu einer vollstandigen Akte aus.",
    statusIdeaAccepted:
      "Dein Konzept ist ausgewahlt. Die Generierung ist akzeptiert und wartet auf den nachsten Schritt.",
    statusMoreIdeas:
      "Wir haben drei weitere Richtungen hinzugefugt. Du hast jetzt {count} Ideen zum Vergleichen.",
    statusFinalIdeas:
      "Wir haben die letzten drei Richtungen hinzugefugt. Du hast jetzt alle {count} Ideen zum Vergleichen.",
    statusDevBypass:
      "Dev-Bypass angewendet. Du kannst den Intake jetzt abschliessen und absenden.",
    orderStatusLoading: "Wird geladen",
    finishSuspectBeforeAdding:
      "Beende Verdachtiger {index}, bevor du einen weiteren hinzufugst.",
    finishSuspectBeforeAddingWithMissing:
      "Beende Verdachtiger {index}, bevor du einen weiteren hinzufugst. Es fehlt: {fields}.",
    devPresetLoaded:
      "Das Dev-Preset {preset} wurde fur schnellere Intake-Tests geladen.",
    statusCheckoutRefreshAfterPayment:
      "Das Zahlungsfenster wurde nach dem Checkout geschlossen. Deine Bestellung wird aktualisiert...",
    statusCheckoutClosedBeforePayment:
      "Der Checkout wurde geschlossen, bevor die Zahlung abgeschlossen war. Offne den Checkout erneut, wenn du bereit bist.",
    checkoutUnavailable: "FastSpring-Checkout ist im Moment nicht verfugbar.",
    statusRestartingBuild:
      "Der Build deines individuellen Falls wird jetzt neu gestartet...",
    statusBuildRestartQueued:
      "Der Build deines individuellen Falls wurde neu gestartet und wieder in die Warteschlange gesetzt.",
    statusBuildRestartAccepted:
      "Der Neustart deines Falls wurde akzeptiert und wartet auf den nachsten Workflow-Schritt.",
    generationRunAttached:
      "Der Lauf {runId} ist mit dieser Bestellung verknupft.",
  },
  it: {
    helmetTitle: "Brief del caso personalizzato | Cold Case Game Kits",
    helmetDescription:
      "Completa il tuo brief privato e inviaci i dettagli necessari per costruire il tuo mistero personalizzato.",
    eyebrow: "Brief del caso personalizzato",
    title: "Inserisci le informazioni per completare il tuo caso",
    ownerAccessTitle: "Verifica l'accesso al tuo brief",
    ownerAccessDescription:
      "Questo brief privato e collegato all'email dell'ordine. Invieremo un codice usa e getta prima di aprire o aggiornare il caso in questo browser.",
    ownerAccessCopy: {
      loading: "Verifica dell'accesso privato...",
      badge: "Accesso proprietario privato",
      sendCodeToOrderEmail: "Invia il codice a",
      rememberedNotice:
        "Lo chiediamo solo su un nuovo browser o dispositivo. Dopo la verifica, questo browser restera memorizzato per le prossime visite.",
      requestIdle: "Invia codice di accesso",
      requestBusy: "Invio del codice...",
      codeLabel: "Codice a 6 cifre",
      verifyIdle: "Verifica e continua",
      verifyBusy: "Verifica in corso...",
      clearRememberedAccess: "Cancella accesso memorizzato",
      devCodePreview: "Codice dev",
      invalidCode: "Inserisci il codice a 6 cifre ricevuto via email.",
      statusCodeSent: "Codice inviato. Inseriscilo qui sotto per continuare.",
      statusVerified: "Accesso confermato per questo browser.",
      statusCleared: "Accesso memorizzato cancellato per questo browser.",
    },
    showOrderDetails: "Mostra dettagli ordine",
    hideOrderDetails: "Nascondi dettagli ordine",
    orderStatusLabel: "Stato ordine",
    paymentLabel: "Pagamento",
    accessTokenLabel: "Token di accesso",
    paymentConfirmedLabel: "Confermato",
    paymentPendingLabel: "In attesa",
    checkPaymentAgain: "Controlla di nuovo il pagamento",
    reopenCheckout: "Riapri il pagamento",
    bypassPaymentInDev: "Salta pagamento in dev",
    confirmingDevPayment: "Conferma pagamento dev...",
    backToCustomCases: "Torna ai casi personalizzati",
    loadingOrder: "Caricamento del tuo ordine personalizzato...",
    paymentBanner:
      "Pagamento confermato. Salva una bozza quando vuoi e inviala quando tutto ti sembra corretto.",
    stepWord: "Fase",
    steps: {
      details: {
        label: "Dettagli evento",
        description:
          "Conferma il tuo nome, la tua email e dove dovrebbe svolgersi il caso.",
      },
      suspects: {
        label: "Sospetti",
        description:
          "Aggiungi persone, ruoli e dettagli fisici che rendono il caso personale.",
      },
      review: {
        label: "Revisione",
        description:
          "Controlla tutto prima di inviare il brief per la generazione.",
      },
    },
    reviewSummary: "Riepilogo ordine",
    suspectReview: "Revisione sospetti",
    firstName: "Nome",
    email: "Email",
    city: "Citta",
    location: "Luogo",
    referralSource: "Fonte",
    suspectsCount: "Sospetti",
    ageSex: "Eta / Sesso",
    hairSkin: "Capelli / Pelle",
    occupation: "Professione",
    relationship: "Relazione",
    notes: "Note",
    notProvided: "Non fornito",
    notSpecified: "Non specificato",
    noExtraNotes: "Nessuna nota extra",
    continueButton: "Continua",
    backButton: "Indietro",
    saveDraftButton: "Salva bozza",
    savingButton: "Salvataggio...",
    submittingButton: "Invio...",
    submitButton: "Le informazioni sono complete, crea il caso personalizzato.",
    generatingIdeasTitle: "Generazione delle tue 3 direzioni di caso",
    generatingIdeasBody:
      "Stiamo usando la tua citta, i sospetti e le loro relazioni per creare opzioni di storia su misura.",
    ideasEyebrow: "Scegli la direzione del caso",
    ideasTitle: "Scegli la storia che vuoi costruire",
    ideasBody:
      "Queste direzioni provengono da diversi framework di mistero scelti in base a citta, sospetti e relazioni. Scegline una e avvieremo la generazione completa.",
    optionLabel: "Opzione",
    selectedLabel: "Selezionata",
    getMoreIdeas: "Ottieni 3 nuove idee",
    getMoreIdeasBusy: "Generazione di 3 nuove idee...",
    getMoreIdeasRemaining: "{count} rimaste",
    queuedLabel: "In coda",
    difficultyLabel: "Difficolta",
    modernThemeLabel: "Moderno",
    classicThemeLabel: "Classico",
    noirThemeLabel: "Noir",
    buildThisCase: "Costruisci questo caso",
    queueing: "Messa in coda...",
    queuedForBuild: "In coda per la generazione",
    selectedIdeaSuffix: "e selezionata.",
    paymentLocked:
      "Il pagamento non e ancora stato confermato, quindi il brief e ancora bloccato. Se hai gia completato il checkout, usa Controlla di nuovo il pagamento. Altrimenti riapri il pagamento e completa l'acquisto.",
    devShortcutTitle: "Scorciatoia per test locali",
    devShortcutBody:
      "Dato che sei in dev, puoi usare Salta pagamento in dev qui sopra per sbloccare il brief senza passare da FastSpring.",
    statusSummaryPending:
      "Non abbiamo ancora confermato il tuo pagamento FastSpring. Se lo hai appena completato, aspetta un momento e ricontrolla.",
    statusSummaryReady:
      "Pagamento confermato. Ora puoi completare e inviare i dettagli del tuo caso personalizzato.",
    statusSummaryIdeasGenerating:
      "Pagamento confermato. Stiamo trasformando il tuo brief in tre direzioni di caso su misura.",
    statusSummaryChooseIdea:
      "Il tuo brief e arrivato. Scegli ora la direzione che vuoi farci costruire.",
    statusSummaryQueued:
      "Il concetto selezionato e ora nella pipeline di generazione. Questa pagina si aggiornnera automaticamente.",
    statusSummaryReviewRequired:
      "La build del vostro caso e terminata, ma ora e in controllo qualita perche abbiamo rilevato elementi da correggere prima della consegna.",
    statusSummaryFailed:
      "L'ultima esecuzione si e fermata prima del completamento. Usa Ricomincia per avviare una nuova esecuzione.",
    statusSummaryComplete:
      "Il concetto selezionato e stato completato e questo ordine ora e completo.",
    statusDraftSaved: "Bozza salvata. Puoi tornare piu tardi per completarla.",
    statusValidationMissing:
      "Mancano ancora alcuni campi obbligatori. Li abbiamo evidenziati per aiutarti a finire rapidamente.",
    statusSubmitted:
      "Il tuo brief e inviato. Ora scegli la direzione che vuoi costruire.",
    statusIdeaQueued:
      "Il tuo caso personalizzato e in coda per la generazione. Costruiremo questo concetto come fascicolo completo.",
    statusIdeaAccepted:
      "Il tuo concetto e selezionato. La generazione e accettata e pronta per il prossimo passaggio.",
    statusMoreIdeas:
      "Abbiamo aggiunto altre tre direzioni. Ora hai {count} idee da confrontare prima di scegliere.",
    statusFinalIdeas:
      "Abbiamo aggiunto le ultime tre direzioni. Ora hai tutte le {count} idee da confrontare prima di scegliere.",
    statusDevBypass:
      "Bypass dev applicato. Ora puoi completare e inviare il brief.",
    orderStatusLoading: "Caricamento",
    finishSuspectBeforeAdding:
      "Completa il sospetto {index} prima di aggiungerne un altro.",
    finishSuspectBeforeAddingWithMissing:
      "Completa il sospetto {index} prima di aggiungerne un altro. Mancano: {fields}.",
    devPresetLoaded:
      "Il preset dev {preset} e stato caricato per testare il brief piu rapidamente.",
    statusCheckoutRefreshAfterPayment:
      "La finestra di pagamento si e chiusa dopo il checkout. Aggiorniamo il tuo ordine...",
    statusCheckoutClosedBeforePayment:
      "Il checkout si e chiuso prima del completamento del pagamento. Riapri il pagamento quando sei pronto.",
    checkoutUnavailable:
      "FastSpring non e pronto per il pagamento in questo momento.",
    statusRestartingBuild:
      "Riavvio della generazione del tuo caso personalizzato...",
    statusBuildRestartQueued:
      "La generazione del tuo caso e stata riavviata e rimessa in coda.",
    statusBuildRestartAccepted:
      "Il riavvio del tuo caso e stato accettato ed e in attesa del prossimo passaggio del flusso.",
    generationRunAttached: "L'esecuzione {runId} e collegata a questo ordine.",
  },
} as const;

const ACTIVE_BUILD_STATUSES = [
  "generation_queued",
  "generation_running",
  "review_required",
] as const;
const COMPLETED_BUILD_STATUSES = ["delivery_ready", "delivered"] as const;
const BUILD_FEED_ROTATION_MS = 4800;
const BUILD_DELAY_NOTICE_THRESHOLD_MS = 4.5 * 60 * 1000;

type BuildFeedContext = {
  caseTitle: string;
  city: string;
  suspects: string[];
};

type BuildActivityPanelCopy = {
  liveProgressEstimate: string;
  progressLabels: {
    queued: string;
    running: string;
    review: string;
    complete: string;
    failed: string;
    preparing: string;
  };
  titles: {
    failed: string;
    review: string;
    complete: string;
    running: string;
  };
  bodies: {
    review: string;
    failed: string;
    complete: string;
    running: string;
  };
  badges: {
    complete: string;
    failed: string;
    review: string;
    running: string;
  };
  autoRefreshing: string;
  liveBuildNotes: string;
  rotatingNotes: string;
  justAdded: string;
  buildNote: string;
  selectedDirection: string;
  city: string;
  caseIdLabel: string;
  runIdLabel: string;
  suspectsBeingWovenIn: string;
  suspectPlaceholder: string;
  milestones: {
    conceptLocked: string;
    fullCaseAssembled: string;
    deliveryReleaseReady: string;
  };
  feedTemplates: Array<(context: BuildFeedContext) => string>;
};

const BUILD_DELAY_NOTICE_COPY: Record<
  CustomLandingLang,
  {
    title: string;
    body: string;
    notifyHeadline: string;
    notifyDescription: string;
    notifySuccessMessage: string;
    notifyNotePlaceholder: string;
    firstNameLabel: string;
    firstNamePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    messageLabel: string;
    notifyIdleLabel: string;
    notifyBusyLabel: string;
  }
> = {
  en: {
    title: "Quick heads-up: this run is taking a little longer.",
    body:
      "Our case engine is working through a few other live builds right now, but your file is still moving. Hold tight. You are usually in the last stretch once the assembly board is this active.",
    notifyHeadline: "If this run keeps dragging, send us your details.",
    notifyDescription:
      "We will receive your email, selected case direction, city, suspects, build status, and timestamp so we can follow up when the case is ready.",
    notifySuccessMessage:
      "We have your details. We will let you know when your case is ready.",
    notifyNotePlaceholder:
      "Optional: tell us anything helpful about this case or what you already picked.",
    firstNameLabel: "First Name",
    firstNamePlaceholder: "Your first name",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    messageLabel: "Message",
    notifyIdleLabel: "Notify Me",
    notifyBusyLabel: "Sending...",
  },
  fr: {
    title: "Petit avertissement : cette execution prend un peu plus de temps.",
    body:
      "Notre moteur de generation traite quelques autres dossiers en direct en ce moment, mais votre affaire continue d'avancer. Tenez bon. En general, vous etes dans la derniere ligne droite quand ce tableau reste aussi actif.",
    notifyHeadline:
      "Si cette execution continue de trainer, envoyez-nous vos coordonnees.",
    notifyDescription:
      "Nous recevrons votre email, la direction choisie, la ville, les suspects, le statut du build et l'horodatage afin de faire le suivi quand l'affaire sera prete.",
    notifySuccessMessage:
      "Nous avons vos coordonnees. Nous vous previendrons quand votre affaire sera prete.",
    notifyNotePlaceholder:
      "Facultatif : dites-nous tout detail utile sur cette affaire ou sur ce que vous avez deja choisi.",
    firstNameLabel: "Prenom",
    firstNamePlaceholder: "Votre prenom",
    emailLabel: "Email",
    emailPlaceholder: "vous@exemple.com",
    messageLabel: "Message",
    notifyIdleLabel: "Me prevenir",
    notifyBusyLabel: "Envoi...",
  },
  es: {
    title: "Aviso rapido: esta ejecucion esta tardando un poco mas.",
    body:
      "Nuestro motor de casos esta resolviendo algunos builds en vivo ahora mismo, pero su expediente sigue avanzando. Aguante un poco mas. Normalmente ya esta en el ultimo tramo cuando el tablero sigue tan activo.",
    notifyHeadline:
      "Si esta ejecucion sigue demorando, envienos sus datos.",
    notifyDescription:
      "Recibiremos su correo, la direccion elegida, la ciudad, los sospechosos, el estado del build y la marca de tiempo para hacer seguimiento cuando el caso este listo.",
    notifySuccessMessage:
      "Ya tenemos sus datos. Le avisaremos cuando su caso este listo.",
    notifyNotePlaceholder:
      "Opcional: cuentenos algo util sobre este caso o sobre lo que ya eligio.",
    firstNameLabel: "Nombre",
    firstNamePlaceholder: "Su nombre",
    emailLabel: "Email",
    emailPlaceholder: "usted@ejemplo.com",
    messageLabel: "Mensaje",
    notifyIdleLabel: "Avisarme",
    notifyBusyLabel: "Enviando...",
  },
  de: {
    title: "Kurzer Hinweis: Dieser Lauf dauert etwas langer.",
    body:
      "Unsere Fall-Engine bearbeitet gerade noch einige andere Live-Builds, aber Ihre Akte lauft weiter. Bitte noch kurz Geduld. Meist ist das schon die letzte Strecke, wenn das Board noch so aktiv ist.",
    notifyHeadline:
      "Wenn sich dieser Lauf weiter zieht, senden Sie uns Ihre Angaben.",
    notifyDescription:
      "Wir erhalten Ihre E-Mail, die gewahlte Fallrichtung, Stadt, Verdachtige, den Build-Status und den Zeitstempel, damit wir Sie benachrichtigen konnen, sobald der Fall bereit ist.",
    notifySuccessMessage:
      "Wir haben Ihre Angaben. Wir melden uns, sobald Ihr Fall bereit ist.",
    notifyNotePlaceholder:
      "Optional: Teilen Sie uns etwas Hilfreiches zu diesem Fall oder Ihrer bisherigen Auswahl mit.",
    firstNameLabel: "Vorname",
    firstNamePlaceholder: "Ihr Vorname",
    emailLabel: "E-Mail",
    emailPlaceholder: "sie@beispiel.de",
    messageLabel: "Nachricht",
    notifyIdleLabel: "Benachrichtigen",
    notifyBusyLabel: "Wird gesendet...",
  },
  it: {
    title: "Avviso rapido: questa esecuzione sta richiedendo un po' piu tempo.",
    body:
      "Il nostro motore sta gestendo anche altri build dal vivo in questo momento, ma il vostro dossier continua ad avanzare. Ancora un po' di pazienza. Di solito siete nell'ultimo tratto quando la bacheca e ancora cosi attiva.",
    notifyHeadline:
      "Se questa esecuzione continua a trascinarsi, inviateci i vostri dati.",
    notifyDescription:
      "Riceveremo la vostra email, la direzione scelta, la citta, i sospetti, lo stato del build e l'orario cosi potremo ricontattarvi quando il caso sara pronto.",
    notifySuccessMessage:
      "Abbiamo ricevuto i vostri dati. Vi avviseremo quando il caso sara pronto.",
    notifyNotePlaceholder:
      "Facoltativo: diteci qualcosa di utile su questo caso o su cio che avete gia scelto.",
    firstNameLabel: "Nome",
    firstNamePlaceholder: "Il vostro nome",
    emailLabel: "Email",
    emailPlaceholder: "voi@esempio.it",
    messageLabel: "Messaggio",
    notifyIdleLabel: "Avvisami",
    notifyBusyLabel: "Invio...",
  },
};

const BUILD_VISUAL_PANEL_COPY: Record<
  CustomLandingLang,
  {
    completedCta: string;
    restarting: string;
    startFresh: string;
    storyboardInMotion: string;
    qualityReviewInProgress: string;
    buildStoppedBeforePersist: string;
    pipelineFinished: string;
    assemblyFramesUpdating: string;
    live: string;
    interrupted: string;
    locked: string;
    customCaseDirectionLocked: string;
    customCity: string;
  }
> = {
  en: {
    completedCta: "Open Your Custom Case",
    restarting: "Restarting...",
    startFresh: "Start Fresh",
    storyboardInMotion: "Storyboard in motion",
    qualityReviewInProgress: "Quality review in progress",
    buildStoppedBeforePersist: "Build stopped before persist",
    pipelineFinished: "Pipeline finished",
    assemblyFramesUpdating: "Assembly Frames Updating",
    live: "Live",
    interrupted: "Interrupted",
    locked: "Locked",
    customCaseDirectionLocked: "Custom case direction locked",
    customCity: "Custom city",
  },
  fr: {
    completedCta: "Ouvrir votre affaire personnalisee",
    restarting: "Redemarrage...",
    startFresh: "Recommencer",
    storyboardInMotion: "Storyboard en mouvement",
    qualityReviewInProgress: "Controle qualite en cours",
    buildStoppedBeforePersist: "Build arrete avant sauvegarde",
    pipelineFinished: "Pipeline termine",
    assemblyFramesUpdating: "Trames d'assemblage en cours",
    live: "En direct",
    interrupted: "Interrompu",
    locked: "Verrouille",
    customCaseDirectionLocked: "Direction de l'affaire verrouillee",
    customCity: "Ville personnalisee",
  },
  es: {
    completedCta: "Abrir su caso personalizado",
    restarting: "Reiniciando...",
    startFresh: "Empezar de nuevo",
    storyboardInMotion: "Storyboard en movimiento",
    qualityReviewInProgress: "Revision de calidad en curso",
    buildStoppedBeforePersist: "Build detenido antes de guardar",
    pipelineFinished: "Pipeline finalizado",
    assemblyFramesUpdating: "Fotogramas de ensamblaje en curso",
    live: "En vivo",
    interrupted: "Interrumpido",
    locked: "Bloqueado",
    customCaseDirectionLocked: "Direccion del caso fijada",
    customCity: "Ciudad personalizada",
  },
  de: {
    completedCta: "Ihren individuellen Fall offnen",
    restarting: "Wird neu gestartet...",
    startFresh: "Neu starten",
    storyboardInMotion: "Storyboard in Bewegung",
    qualityReviewInProgress: "Qualitatsprufung lauft",
    buildStoppedBeforePersist: "Build vor dem Speichern gestoppt",
    pipelineFinished: "Pipeline abgeschlossen",
    assemblyFramesUpdating: "Montagebilder werden aktualisiert",
    live: "Live",
    interrupted: "Unterbrochen",
    locked: "Fixiert",
    customCaseDirectionLocked: "Fallrichtung festgelegt",
    customCity: "Individuelle Stadt",
  },
  it: {
    completedCta: "Apri il vostro caso personalizzato",
    restarting: "Riavvio...",
    startFresh: "Ricomincia",
    storyboardInMotion: "Storyboard in movimento",
    qualityReviewInProgress: "Controllo qualita in corso",
    buildStoppedBeforePersist: "Build fermato prima del salvataggio",
    pipelineFinished: "Pipeline completata",
    assemblyFramesUpdating: "Frame di assemblaggio in aggiornamento",
    live: "Live",
    interrupted: "Interrotto",
    locked: "Bloccato",
    customCaseDirectionLocked: "Direzione del caso bloccata",
    customCity: "Citta personalizzata",
  },
};

const buildFeedValue = (value: string, fallback: string) =>
  String(value || "").trim() || fallback;

const BUILD_ACTIVITY_PANEL_COPY: Record<
  CustomLandingLang,
  BuildActivityPanelCopy
> = {
  en: {
    liveProgressEstimate: "Live Progress Estimate",
    progressLabels: {
      queued: "Queued for assembly",
      running: "Case assembly in motion",
      review: "Quality review in progress",
      complete: "Build complete",
      failed: "Build interrupted",
      preparing: "Preparing your case",
    },
    titles: {
      failed: "This build needs to be restarted",
      review: "Quality review is underway",
      complete: "Your case build is complete",
      running: "We are building your case",
    },
    bodies: {
      review:
        "Your case has been assembled, but our quality checks caught issues we need to fix before release. We are holding delivery here rather than sending a broken dossier.",
      failed:
        "The last generation run stopped before the case could be persisted. Use Start fresh to queue a new run from this order.",
      complete:
        "The story, suspect packet, and full case structure are assembled. This order is now marked complete in the build pipeline.",
      running:
        "We are turning the selected concept into a full dossier with suspects, evidence structure, and the final reveal.",
    },
    badges: {
      complete: "Build Complete",
      failed: "Build Stopped",
      review: "Quality Review",
      running: "Case Assembly Active",
    },
    autoRefreshing: "auto refreshing",
    liveBuildNotes: "Live Build Notes",
    rotatingNotes:
      "Rotating assembly updates while your case is being built.",
    justAdded: "just added",
    buildNote: "build note",
    selectedDirection: "Selected Direction",
    city: "City",
    caseIdLabel: "Case ID",
    runIdLabel: "Run ID",
    suspectsBeingWovenIn: "Suspects Being Woven In",
    suspectPlaceholder:
      "Your suspect list will appear here as soon as names are saved.",
    milestones: {
      conceptLocked: "Concept locked",
      fullCaseAssembled: "Full case assembled",
      deliveryReleaseReady: "Delivery release ready",
    },
    feedTemplates: [
      ({ city }) =>
        `Building the cold case now around the murder in ${buildFeedValue(city, "your chosen city")}.`,
      ({ caseTitle }) =>
        `Building a solid storyline for ${buildFeedValue(caseTitle, "your custom case")}.`,
      ({ suspects }) =>
        `Adding ${buildFeedValue(suspects[0] || "", "the first suspect")} to the master suspect roster.`,
      ({ suspects }) =>
        `Weaving ${buildFeedValue(suspects[1] || suspects[0] || "", "another suspect")} into the motive web.`,
      ({ city, suspects }) =>
        `Cross-checking where ${buildFeedValue(suspects[0] || "", "the lead suspect")} was seen in ${buildFeedValue(city, "the city")} that night.`,
      ({ caseTitle }) =>
        `Drafting opening briefing notes for ${buildFeedValue(caseTitle, "this case file")}.`,
      ({ suspects }) =>
        `Drafting red herrings that keep ${buildFeedValue(suspects[1] || suspects[0] || "", "the suspect list")} in play.`,
      ({ suspects }) =>
        `Building dossier details, visual traits, and story hooks for ${buildFeedValue(suspects[0] || "", "your suspect")}.`,
    ],
  },
  fr: {
    liveProgressEstimate: "Estimation de progression en direct",
    progressLabels: {
      queued: "Mise en file pour l'assemblage",
      running: "Assemblage de l'affaire en cours",
      review: "Controle qualite en cours",
      complete: "Assemblage termine",
      failed: "Assemblage interrompu",
      preparing: "Preparation de votre affaire",
    },
    titles: {
      failed: "Cette generation doit etre relancee",
      review: "Le controle qualite est en cours",
      complete: "Votre affaire personnalisee est prete",
      running: "Nous construisons votre affaire",
    },
    bodies: {
      review:
        "Votre affaire est assemblee, mais nos controles qualite ont releve quelques points a corriger avant la livraison. Nous preferons la retenir ici plutot que de publier un dossier imparfait.",
      failed:
        "La derniere generation s'est arretee avant la sauvegarde complete du dossier. Utilisez Recommencer pour lancer une nouvelle execution.",
      complete:
        "L'histoire, le dossier des suspects et la structure complete de l'affaire sont assembles. Cette commande est maintenant terminee dans le pipeline.",
      running:
        "Nous transformons le concept choisi en dossier complet avec suspects, structure des preuves et revelation finale.",
    },
    badges: {
      complete: "Assemblage termine",
      failed: "Assemblage arrete",
      review: "Controle qualite",
      running: "Assemblage actif",
    },
    autoRefreshing: "actualisation automatique",
    liveBuildNotes: "Notes d'assemblage",
    rotatingNotes:
      "Mises a jour rotatives pendant la construction de votre affaire.",
    justAdded: "ajoute a l'instant",
    buildNote: "note d'assemblage",
    selectedDirection: "Direction retenue",
    city: "Ville",
    caseIdLabel: "ID du dossier",
    runIdLabel: "ID d'execution",
    suspectsBeingWovenIn: "Suspects integres au dossier",
    suspectPlaceholder:
      "Votre liste de suspects apparaitra ici des que les noms seront enregistres.",
    milestones: {
      conceptLocked: "Concept verrouille",
      fullCaseAssembled: "Affaire assemblee",
      deliveryReleaseReady: "Livraison prete",
    },
    feedTemplates: [
      ({ city }) =>
        `Construction du dossier autour du meurtre a ${buildFeedValue(city, "votre ville choisie")}.`,
      ({ caseTitle }) =>
        `Mise en place d'une intrigue solide pour ${buildFeedValue(caseTitle, "votre affaire personnalisee")}.`,
      ({ suspects }) =>
        `Ajout de ${buildFeedValue(suspects[0] || "", "votre premier suspect")} au registre principal des suspects.`,
      ({ suspects }) =>
        `Integration de ${buildFeedValue(suspects[1] || suspects[0] || "", "un autre suspect")} dans le reseau des mobiles.`,
      ({ city, suspects }) =>
        `Verification de la presence de ${buildFeedValue(suspects[0] || "", "votre suspect principal")} a ${buildFeedValue(city, "la ville")} cette nuit-la.`,
      ({ caseTitle }) =>
        `Redaction des premieres notes de briefing pour ${buildFeedValue(caseTitle, "ce dossier")}.`,
      ({ suspects }) =>
        `Preparation de fausses pistes pour maintenir ${buildFeedValue(suspects[1] || suspects[0] || "", "la liste des suspects")} en jeu.`,
      ({ suspects }) =>
        `Assemblage des details du dossier, des traits visuels et des accroches pour ${buildFeedValue(suspects[0] || "", "votre suspect")}.`,
    ],
  },
  es: {
    liveProgressEstimate: "Estimacion de progreso en vivo",
    progressLabels: {
      queued: "En cola para ensamblaje",
      running: "Ensamblaje del caso en marcha",
      review: "Revision de calidad en curso",
      complete: "Caso completado",
      failed: "Ensamblaje interrumpido",
      preparing: "Preparando su caso",
    },
    titles: {
      failed: "Esta generacion debe reiniciarse",
      review: "La revision de calidad esta en curso",
      complete: "La construccion de su caso esta completa",
      running: "Estamos construyendo su caso",
    },
    bodies: {
      review:
        "Su caso ya esta ensamblado, pero nuestros controles de calidad detectaron puntos que debemos corregir antes de liberarlo. Preferimos detenernos aqui antes que entregar un dossier defectuoso.",
      failed:
        "La ultima ejecucion se detuvo antes de guardar el caso completo. Use Empezar de nuevo para lanzar una nueva ejecucion desde este pedido.",
      complete:
        "La historia, el paquete de sospechosos y la estructura completa del caso ya estan ensamblados. Este pedido ahora figura como completo en el pipeline.",
      running:
        "Estamos convirtiendo el concepto elegido en un dossier completo con sospechosos, estructura de pruebas y revelacion final.",
    },
    badges: {
      complete: "Caso completo",
      failed: "Construccion detenida",
      review: "Revision de calidad",
      running: "Ensamblaje activo",
    },
    autoRefreshing: "actualizacion automatica",
    liveBuildNotes: "Notas de construccion",
    rotatingNotes:
      "Actualizaciones rotativas mientras se construye su caso.",
    justAdded: "recien agregado",
    buildNote: "nota de construccion",
    selectedDirection: "Direccion elegida",
    city: "Ciudad",
    caseIdLabel: "ID del caso",
    runIdLabel: "ID de ejecucion",
    suspectsBeingWovenIn: "Sospechosos integrados",
    suspectPlaceholder:
      "Su lista de sospechosos aparecera aqui en cuanto se guarden los nombres.",
    milestones: {
      conceptLocked: "Concepto fijado",
      fullCaseAssembled: "Caso ensamblado",
      deliveryReleaseReady: "Entrega lista",
    },
    feedTemplates: [
      ({ city }) =>
        `Construyendo el expediente alrededor del asesinato en ${buildFeedValue(city, "la ciudad elegida")}.`,
      ({ caseTitle }) =>
        `Dando forma a una linea argumental solida para ${buildFeedValue(caseTitle, "su caso personalizado")}.`,
      ({ suspects }) =>
        `Agregando a ${buildFeedValue(suspects[0] || "", "el primer sospechoso")} al registro principal.`,
      ({ suspects }) =>
        `Integrando a ${buildFeedValue(suspects[1] || suspects[0] || "", "otro sospechoso")} en la red de motivos.`,
      ({ city, suspects }) =>
        `Comprobando donde fue visto ${buildFeedValue(suspects[0] || "", "el sospechoso principal")} en ${buildFeedValue(city, "la ciudad")} esa noche.`,
      ({ caseTitle }) =>
        `Redactando las notas iniciales del briefing para ${buildFeedValue(caseTitle, "este expediente")}.`,
      ({ suspects }) =>
        `Preparando pistas falsas para mantener a ${buildFeedValue(suspects[1] || suspects[0] || "", "la lista de sospechosos")} en juego.`,
      ({ suspects }) =>
        `Armando detalles de dossier, rasgos visuales y ganchos narrativos para ${buildFeedValue(suspects[0] || "", "su sospechoso")}.`,
    ],
  },
  de: {
    liveProgressEstimate: "Live-Fortschrittsanzeige",
    progressLabels: {
      queued: "Zur Fallmontage vorgemerkt",
      running: "Fallmontage lauft",
      review: "Qualitatsprufung lauft",
      complete: "Fall fertiggestellt",
      failed: "Montage unterbrochen",
      preparing: "Ihr Fall wird vorbereitet",
    },
    titles: {
      failed: "Dieser Build muss neu gestartet werden",
      review: "Die Qualitatsprufung lauft",
      complete: "Ihr Fallaufbau ist abgeschlossen",
      running: "Wir bauen Ihren Fall auf",
    },
    bodies: {
      review:
        "Ihr Fall ist aufgebaut, aber unsere Qualitatsprufungen haben Punkte gefunden, die wir vor der Freigabe korrigieren mussen. Wir halten die Auslieferung hier lieber an, als eine fehlerhafte Akte zu veroffentlichen.",
      failed:
        "Der letzte Lauf wurde beendet, bevor der Fall vollstandig gespeichert werden konnte. Nutzen Sie Neu starten fur einen sauberen neuen Lauf.",
      complete:
        "Story, Verdachtigenpaket und vollstandige Fallstruktur sind aufgebaut. Dieser Auftrag ist im Pipeline-Status nun abgeschlossen.",
      running:
        "Wir verwandeln das gewahlte Konzept in ein vollstandiges Dossier mit Verdachtigen, Beweisstruktur und finaler Auflosung.",
    },
    badges: {
      complete: "Build abgeschlossen",
      failed: "Build gestoppt",
      review: "Qualitatsprufung",
      running: "Fallmontage aktiv",
    },
    autoRefreshing: "automatische aktualisierung",
    liveBuildNotes: "Montagenotizen",
    rotatingNotes:
      "Rotierende Statusmeldungen, wahrend Ihr Fall aufgebaut wird.",
    justAdded: "gerade hinzugefugt",
    buildNote: "montagehinweis",
    selectedDirection: "Gewahlte Richtung",
    city: "Stadt",
    caseIdLabel: "Fall-ID",
    runIdLabel: "Lauf-ID",
    suspectsBeingWovenIn: "Eingearbeitete Verdachtige",
    suspectPlaceholder:
      "Ihre Verdachtigenliste erscheint hier, sobald Namen gespeichert wurden.",
    milestones: {
      conceptLocked: "Konzept fixiert",
      fullCaseAssembled: "Fall aufgebaut",
      deliveryReleaseReady: "Freigabe bereit",
    },
    feedTemplates: [
      ({ city }) =>
        `Der Fall wird jetzt rund um den Mord in ${buildFeedValue(city, "Ihrer gewahlten Stadt")} aufgebaut.`,
      ({ caseTitle }) =>
        `Eine tragfahige Handlung fur ${buildFeedValue(caseTitle, "Ihren individuellen Fall")} wird ausgearbeitet.`,
      ({ suspects }) =>
        ` ${buildFeedValue(suspects[0] || "", "Der erste Verdachtige")} wird in die Hauptverdachtigenliste aufgenommen.`,
      ({ suspects }) =>
        `${buildFeedValue(suspects[1] || suspects[0] || "", "Ein weiterer Verdachtiger")} wird in das Motivgeflecht eingewoben.`,
      ({ city, suspects }) =>
        `Es wird gepruft, wo ${buildFeedValue(suspects[0] || "", "der Hauptverdachtige")} in ${buildFeedValue(city, "der Stadt")} in jener Nacht gesehen wurde.`,
      ({ caseTitle }) =>
        `Die ersten Briefing-Notizen fur ${buildFeedValue(caseTitle, "diese Akte")} werden ausgearbeitet.`,
      ({ suspects }) =>
        `Ablenkungsspuren werden vorbereitet, damit ${buildFeedValue(suspects[1] || suspects[0] || "", "die Verdachtigenliste")} lange offen bleibt.`,
      ({ suspects }) =>
        `Dossierdetails, visuelle Merkmale und Story-Hooks fur ${buildFeedValue(suspects[0] || "", "Ihren Verdachtigen")} werden ausgearbeitet.`,
    ],
  },
  it: {
    liveProgressEstimate: "Stima dei progressi in diretta",
    progressLabels: {
      queued: "In coda per l'assemblaggio",
      running: "Assemblaggio del caso in corso",
      review: "Controllo qualita in corso",
      complete: "Caso completato",
      failed: "Assemblaggio interrotto",
      preparing: "Preparazione del caso",
    },
    titles: {
      failed: "Questa generazione deve essere riavviata",
      review: "Il controllo qualita e in corso",
      complete: "La costruzione del caso e completa",
      running: "Stiamo costruendo il vostro caso",
    },
    bodies: {
      review:
        "Il vostro caso e stato assemblato, ma i nostri controlli qualita hanno individuato punti da correggere prima del rilascio. Preferiamo fermarci qui invece di consegnare un dossier difettoso.",
      failed:
        "L'ultima esecuzione si e fermata prima che il caso venisse salvato del tutto. Usate Ricomincia per avviare una nuova esecuzione pulita.",
      complete:
        "La storia, il pacchetto dei sospetti e l'intera struttura del caso sono assemblati. Questo ordine ora risulta completo nella pipeline.",
      running:
        "Stiamo trasformando il concetto scelto in un dossier completo con sospetti, struttura delle prove e rivelazione finale.",
    },
    badges: {
      complete: "Assemblaggio completato",
      failed: "Assemblaggio fermato",
      review: "Controllo qualita",
      running: "Assemblaggio attivo",
    },
    autoRefreshing: "aggiornamento automatico",
    liveBuildNotes: "Note di assemblaggio",
    rotatingNotes:
      "Aggiornamenti rotanti mentre il vostro caso viene costruito.",
    justAdded: "appena aggiunto",
    buildNote: "nota di assemblaggio",
    selectedDirection: "Direzione selezionata",
    city: "Citta",
    caseIdLabel: "ID caso",
    runIdLabel: "ID esecuzione",
    suspectsBeingWovenIn: "Sospetti integrati",
    suspectPlaceholder:
      "L'elenco dei sospetti comparira qui non appena i nomi saranno salvati.",
    milestones: {
      conceptLocked: "Concept fissato",
      fullCaseAssembled: "Caso assemblato",
      deliveryReleaseReady: "Consegna pronta",
    },
    feedTemplates: [
      ({ city }) =>
        `Stiamo costruendo il dossier attorno all'omicidio a ${buildFeedValue(city, "la citta scelta")}.`,
      ({ caseTitle }) =>
        `Stiamo costruendo una trama solida per ${buildFeedValue(caseTitle, "il vostro caso personalizzato")}.`,
      ({ suspects }) =>
        `Aggiunta di ${buildFeedValue(suspects[0] || "", "il primo sospetto")} al registro principale dei sospetti.`,
      ({ suspects }) =>
        `Inserimento di ${buildFeedValue(suspects[1] || suspects[0] || "", "un altro sospetto")} nella rete dei moventi.`,
      ({ city, suspects }) =>
        `Verifica di dove sia stato visto ${buildFeedValue(suspects[0] || "", "il sospetto principale")} a ${buildFeedValue(city, "la citta")} quella notte.`,
      ({ caseTitle }) =>
        `Stesura delle note iniziali di briefing per ${buildFeedValue(caseTitle, "questo dossier")}.`,
      ({ suspects }) =>
        `Preparazione di false piste per mantenere ${buildFeedValue(suspects[1] || suspects[0] || "", "la rosa dei sospetti")} in gioco.`,
      ({ suspects }) =>
        `Costruzione di dettagli di dossier, tratti visivi e ganci narrativi per ${buildFeedValue(suspects[0] || "", "il vostro sospetto")}.`,
    ],
  },
};

const shuffleBuildFeedMessages = (messages: string[]) => {
  const nextMessages = [...messages];
  for (let index = nextMessages.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [nextMessages[index], nextMessages[swapIndex]] = [
      nextMessages[swapIndex],
      nextMessages[index],
    ];
  }
  return nextMessages;
};

const buildCaseAssemblyFeedMessages = ({
  caseTitle,
  city,
  suspects,
  lang,
}: BuildFeedContext & { lang: CustomLandingLang }) =>
  shuffleBuildFeedMessages(
    (BUILD_ACTIVITY_PANEL_COPY[lang] || BUILD_ACTIVITY_PANEL_COPY.en).feedTemplates.map((template) =>
      template({ caseTitle, city, suspects }),
    ).filter(Boolean),
  );

const replaceCountToken = (value: string, count: number) =>
  String(value || "").replace("{count}", String(count));

const replaceTemplateTokens = (
  value: string,
  replacements: Record<string, string | number>,
) =>
  Object.entries(replacements).reduce(
    (current, [key, replacement]) =>
      current.replace(new RegExp(`\\{${key}\\}`, "g"), String(replacement)),
    String(value || ""),
  );

const localizeIdeaThemeLabel = (
  theme: string,
  copy: (typeof INTAKE_PAGE_COPY)["en"],
) => {
  const normalizedTheme = String(theme || "modern").trim().toLowerCase();
  if (normalizedTheme === "classic") return copy.classicThemeLabel;
  if (normalizedTheme === "noir") return copy.noirThemeLabel;
  return copy.modernThemeLabel;
};

const resolveOrderStatusMessage = (
  order: any,
  copy: (typeof INTAKE_PAGE_COPY)["en"],
) => {
  const nextStatus = String(order?.status || "").trim();

  if (nextStatus === "ideas_generating") {
    return copy.statusSummaryIdeasGenerating;
  }
  if (nextStatus === "generation_failed") {
    return copy.statusSummaryFailed;
  }
  if (
    Array.isArray(order?.generatedIdeas) &&
    order.generatedIdeas.length > 0 &&
    !ACTIVE_BUILD_STATUSES.includes(nextStatus as any) &&
    !COMPLETED_BUILD_STATUSES.includes(nextStatus as any)
  ) {
    return copy.statusSummaryChooseIdea;
  }
  if (nextStatus === "generation_queued") {
    return copy.statusSummaryQueued;
  }
  if (nextStatus === "generation_running") {
    return copy.statusSummaryQueued;
  }
  if (nextStatus === "review_required") {
    return copy.statusSummaryReviewRequired;
  }
  if (nextStatus === "delivery_ready" || nextStatus === "delivered") {
    return copy.statusSummaryComplete;
  }
  if (order?.paymentConfirmedAt) {
    return copy.statusSummaryReady;
  }
  return copy.statusSummaryPending;
};

const parseTimestampMs = (value: unknown) => {
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatElapsedBuildTime = (elapsedMs: number) => {
  const safeElapsedMs = Math.max(0, Number(elapsedMs || 0));
  const totalSeconds = Math.floor(safeElapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${seconds}s`;
};

const getBuildProgressEstimate = (
  orderStatus: string,
  elapsedMs: number,
  copy: BuildActivityPanelCopy,
) => {
  if (orderStatus === "generation_queued") {
    return { percent: 22, label: copy.progressLabels.queued };
  }
  if (orderStatus === "generation_running") {
    const elapsedMinutes = Math.max(0, elapsedMs / 60000);
    return {
      percent: Math.min(84, Math.round(46 + elapsedMinutes * 9)),
      label: copy.progressLabels.running,
    };
  }
  if (orderStatus === "review_required") {
    return { percent: 93, label: copy.progressLabels.review };
  }
  if (orderStatus === "delivery_ready" || orderStatus === "delivered") {
    return { percent: 100, label: copy.progressLabels.complete };
  }
  if (orderStatus === "generation_failed") {
    return { percent: 64, label: copy.progressLabels.failed };
  }
  return { percent: 18, label: copy.progressLabels.preparing };
};

const BuildActivityPanel = ({
  accessToken,
  orderStatus,
  generatedCaseId,
  generationRunId,
  buildStartedAt,
  isPolling,
  caseTitle,
  city,
  suspects,
  deliveryUrl,
  onStartFresh,
  isRestartingFailedBuild,
  lang,
}: {
  accessToken: string;
  orderStatus: string;
  generatedCaseId: string;
  generationRunId: string;
  buildStartedAt: string;
  isPolling: boolean;
  caseTitle: string;
  city: string;
  suspects: string[];
  deliveryUrl: string;
  onStartFresh: () => void;
  isRestartingFailedBuild: boolean;
  lang: CustomLandingLang;
}) => {
  const panelCopy = BUILD_ACTIVITY_PANEL_COPY[lang] || BUILD_ACTIVITY_PANEL_COPY.en;
  const delayCopy = BUILD_DELAY_NOTICE_COPY[lang] || BUILD_DELAY_NOTICE_COPY.en;
  const visualCopy = BUILD_VISUAL_PANEL_COPY[lang] || BUILD_VISUAL_PANEL_COPY.en;
  const isCompleted = COMPLETED_BUILD_STATUSES.includes(orderStatus as any);
  const isFailed = orderStatus === "generation_failed";
  const isReviewRequired = orderStatus === "review_required";
  const isBuildFeedActive = !isCompleted && !isFailed && !isReviewRequired;
  const isAnimated = !isCompleted && !isFailed && !isReviewRequired;
  const title = isFailed
    ? panelCopy.titles.failed
    : isReviewRequired
      ? panelCopy.titles.review
      : isCompleted
        ? panelCopy.titles.complete
        : panelCopy.titles.running;
  const body =
    isReviewRequired
      ? panelCopy.bodies.review
      : isFailed
        ? panelCopy.bodies.failed
        : isCompleted
          ? panelCopy.bodies.complete
          : panelCopy.bodies.running;
  const visibleSuspects = useMemo(
    () => suspects.filter(Boolean).slice(0, 6),
    [suspects],
  );
  const buildStartedAtMs = useMemo(
    () => parseTimestampMs(buildStartedAt),
    [buildStartedAt],
  );
  const [nowMs, setNowMs] = useState(() => Date.now());
  const delayNoticeLoggedRef = useRef(false);
  const reviewRequiredLoggedRef = useRef(false);
  const elapsedMs =
    buildStartedAtMs > 0 ? Math.max(0, nowMs - buildStartedAtMs) : 0;
  const elapsedLabel =
    buildStartedAtMs > 0 ? formatElapsedBuildTime(elapsedMs) : "";
  const progressEstimate = useMemo(
    () => getBuildProgressEstimate(orderStatus, elapsedMs, panelCopy),
    [elapsedMs, orderStatus, panelCopy],
  );
  const showDelayNotice =
    isBuildFeedActive &&
    buildStartedAtMs > 0 &&
    elapsedMs >= BUILD_DELAY_NOTICE_THRESHOLD_MS;
  const feedMessages = useMemo(
    () =>
      buildCaseAssemblyFeedMessages({
        caseTitle,
        city,
        suspects: visibleSuspects,
        lang,
      }),
    [caseTitle, city, lang, visibleSuspects],
  );
  const feedPointerRef = useRef(0);
  const feedEntryIdRef = useRef(0);
  const [buildFeedEntries, setBuildFeedEntries] = useState<
    Array<{ id: number; text: string }>
  >([]);

  useEffect(() => {
    if (!isBuildFeedActive || buildStartedAtMs <= 0) return;

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [buildStartedAtMs, isBuildFeedActive]);

  useEffect(() => {
    if (!showDelayNotice || delayNoticeLoggedRef.current) return;

    delayNoticeLoggedRef.current = true;
    devLog("route", "custom_case_build_delay_notice_shown", {
      route: "/custom/intake/:accessToken",
      orderStatus,
      elapsedMs,
      generationRunId,
      generatedCaseId,
    });
  }, [
    elapsedMs,
    generatedCaseId,
    generationRunId,
    orderStatus,
    showDelayNotice,
  ]);

  useEffect(() => {
    if (!isReviewRequired || reviewRequiredLoggedRef.current) return;

    reviewRequiredLoggedRef.current = true;
    devLog("route", "custom_case_review_required_panel_shown", {
      route: "/custom/intake/:accessToken",
      orderStatus,
      generationRunId,
      generatedCaseId,
      elapsedMs,
    });
  }, [
    elapsedMs,
    generatedCaseId,
    generationRunId,
    isReviewRequired,
    orderStatus,
  ]);

  useEffect(() => {
    if (!feedMessages.length) {
      setBuildFeedEntries([]);
      return;
    }

    feedPointerRef.current = 0;
    const initialEntries = feedMessages
      .slice(0, Math.min(4, feedMessages.length))
      .map((text) => ({
        id: feedEntryIdRef.current++,
        text,
      }));
    setBuildFeedEntries(initialEntries);
    feedPointerRef.current = initialEntries.length % feedMessages.length;
    devLog("route", "custom_case_build_feed_initialized", {
      route: "/custom/intake/:accessToken",
      orderStatus,
      messageTemplateCount: feedMessages.length,
      suspectCount: visibleSuspects.length,
      city: city || "",
      caseTitle: caseTitle || "",
    });

    if (!isBuildFeedActive || feedMessages.length < 2) return;

    devLog("route", "custom_case_build_feed_rotation_started", {
      route: "/custom/intake/:accessToken",
      orderStatus,
      intervalMs: BUILD_FEED_ROTATION_MS,
      messageTemplateCount: feedMessages.length,
    });

    const intervalId = window.setInterval(() => {
      const nextMessage =
        feedMessages[feedPointerRef.current % feedMessages.length];
      feedPointerRef.current =
        (feedPointerRef.current + 1) % feedMessages.length;
      setBuildFeedEntries((current) => [
        ...current.slice(-4),
        {
          id: feedEntryIdRef.current++,
          text: nextMessage,
        },
      ]);
    }, BUILD_FEED_ROTATION_MS);

    return () => {
      window.clearInterval(intervalId);
      devLog("route", "custom_case_build_feed_rotation_stopped", {
        route: "/custom/intake/:accessToken",
        orderStatus,
      });
    };
  }, [
    caseTitle,
    city,
    feedMessages,
    isBuildFeedActive,
    orderStatus,
    visibleSuspects.length,
  ]);

  return (
    <div
      className={`mt-6 overflow-hidden rounded-[32px] border p-5 sm:p-6 lg:p-8 ${
        isCompleted
          ? "border-emerald-300/25 bg-[radial-gradient(circle_at_20%_0%,rgba(52,211,153,0.18),rgba(7,10,18,0.96)_44%),linear-gradient(135deg,rgba(7,10,18,0.98),rgba(8,21,19,0.96))]"
          : "border-crimson/20 bg-[radial-gradient(circle_at_20%_0%,rgba(220,38,38,0.18),rgba(7,10,18,0.96)_42%),linear-gradient(135deg,rgba(7,10,18,0.98),rgba(16,11,18,0.96))]"
      }`}
    >
      <style>{`
        @keyframes case-build-rail-sweep {
          0% { transform: translateX(-130%); opacity: 0; }
          18% { opacity: 1; }
          82% { opacity: 1; }
          100% { transform: translateX(310%); opacity: 0; }
        }
        @keyframes case-build-card-drift {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.9; }
          50% { transform: translateY(-8px) scale(1.01); opacity: 1; }
        }
        @keyframes case-build-scanline {
          0% { transform: translateX(-120%); opacity: 0; }
          25% { opacity: 0.85; }
          100% { transform: translateX(220%); opacity: 0; }
        }
        @keyframes case-build-clock-tick {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(10deg); }
          75% { transform: rotate(-10deg); }
        }
      `}</style>
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-[0.22em] text-white/52">
          <span>{panelCopy.liveProgressEstimate}</span>
          <span>{`${progressEstimate.label}${elapsedLabel ? ` • ${elapsedLabel}` : ""}`}</span>
        </div>
        <div className="relative mt-3 h-2.5 overflow-hidden rounded-full bg-white/7">
          <div
            className={`absolute inset-y-0 left-0 rounded-full ${
              isCompleted
                ? "bg-emerald-400/80"
                : isFailed
                  ? "bg-amber-300/70"
                  : "bg-[linear-gradient(90deg,rgba(220,38,38,0.45),rgba(244,114,182,0.72),rgba(94,214,181,0.9))]"
            }`}
            style={{ width: `${progressEstimate.percent}%` }}
          />
          {isAnimated ? (
            <div
              className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.7),transparent)] mix-blend-screen"
              style={{
                animation: "case-build-rail-sweep 2.8s linear infinite",
              }}
            />
          ) : null}
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr] xl:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-white/70">
            {isCompleted ? (
              <FileCheck2 className="h-3.5 w-3.5" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {isCompleted
              ? panelCopy.badges.complete
              : isFailed
                ? panelCopy.badges.failed
                : isReviewRequired
                  ? panelCopy.badges.review
                : panelCopy.badges.running}
          </div>
          <h3 className={`font-heading ${CASE_VIEW_PANEL_HEADING_CLASS}`}>
            {title}
          </h3>
          <p
            className={`mt-3 max-w-2xl text-white/76 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
          >
            {body}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <span className="rounded-full border border-white/12 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/72">
              {progressEstimate.label}
            </span>
            {isPolling ? (
              <span className="rounded-full border border-white/12 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/72">
                {panelCopy.autoRefreshing}
              </span>
            ) : null}
          </div>

          {isBuildFeedActive && buildFeedEntries.length > 0 ? (
            <div className="mt-5 overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8ddfcb]">
                    {panelCopy.liveBuildNotes}
                  </p>
                  <p className="mt-1 text-xs text-white/45">
                    {panelCopy.rotatingNotes}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className="h-2 w-2 rounded-full bg-[#5ed6b5]/70 animate-pulse"
                      style={{ animationDelay: `${dot * 180}ms` }}
                    />
                  ))}
                </div>
              </div>
              <div className="relative h-[220px] overflow-y-auto px-4 py-3">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-[rgba(7,10,18,0.9)] to-transparent" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[rgba(7,10,18,0.94)] to-transparent" />
                <div className="space-y-3">
                  {buildFeedEntries.map((entry, index) => {
                    const isLatest = index === buildFeedEntries.length - 1;
                    return (
                      <div
                        key={entry.id}
                        className={`rounded-2xl border px-3 py-3 transition ${
                          isLatest
                            ? "border-[#5ed6b5]/35 bg-[#0b1d1d]/80 shadow-[0_0_0_1px_rgba(94,214,181,0.06)]"
                            : "border-white/8 bg-black/16"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 h-2.5 w-2.5 rounded-full ${
                              isLatest
                                ? "bg-[#5ed6b5] shadow-[0_0_18px_rgba(94,214,181,0.55)]"
                                : "bg-white/25"
                            }`}
                          />
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-white/38">
                              {isLatest
                                ? panelCopy.justAdded
                                : panelCopy.buildNote}
                            </p>
                            <p
                              className={`mt-1 ${CASE_VIEW_SUPPORTING_READING_CLASS} ${
                                isLatest ? "text-white" : "text-white/68"
                              }`}
                            >
                              {entry.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">
                {panelCopy.selectedDirection}
              </p>
              <p className="mt-3 text-lg font-semibold font-heading text-white sm:text-xl">
                {caseTitle || visualCopy.customCaseDirectionLocked}
              </p>
            </div>
            <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">
                {panelCopy.city}
              </p>
              <div className="mt-3 inline-flex items-center gap-2 text-white">
                <MapPinned className="h-4 w-4 text-[#d6b26b]" />
                <span className="text-base font-semibold">
                  {city || visualCopy.customCity}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-[24px] border border-white/10 bg-white/[0.04] p-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/45">
              {panelCopy.suspectsBeingWovenIn}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {visibleSuspects.length > 0 ? (
                visibleSuspects.map((suspect) => (
                  <span
                    key={suspect}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/84"
                  >
                    <UserRound className="h-3.5 w-3.5 text-[#d6b26b]" />
                    {suspect}
                  </span>
                ))
              ) : (
                <span className="text-sm text-white/60">
                  {panelCopy.suspectPlaceholder}
                </span>
              )}
            </div>
          </div>

          {isCompleted && deliveryUrl ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={deliveryUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-400"
              >
                <ExternalLink className="h-4 w-4" />
                {visualCopy.completedCta}
              </a>
            </div>
          ) : isFailed ? (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onStartFresh}
                disabled={isRestartingFailedBuild}
                className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-5 py-3 text-sm font-semibold text-white/88 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRestartingFailedBuild
                  ? visualCopy.restarting
                  : visualCopy.startFresh}
              </button>
            </div>
          ) : null}
        </div>

        <div className="rounded-[28px] border border-white/10 bg-black/25 p-4 sm:p-5">
          {showDelayNotice ? (
            <div className="mb-4 rounded-[24px] border border-amber-300/20 bg-[linear-gradient(135deg,rgba(245,158,11,0.12),rgba(255,255,255,0.03))] p-4 text-white/88">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-400/10 text-amber-100">
                  <Clock3
                    className="h-5 w-5"
                    style={{
                      animation:
                        "case-build-clock-tick 1.6s ease-in-out infinite",
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white">
                    {delayCopy.title}
                  </p>
                  <p
                    className={`mt-1 text-white/72 ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
                  >
                    {delayCopy.body}
                  </p>
                </div>
                {elapsedLabel ? (
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/58">
                    {elapsedLabel}
                  </div>
                ) : null}
              </div>
              <div className="mt-4">
                <CustomCaseNotifyMePanel
                  compact
                  sourceContext="custom_case_build_delay_notice"
                  technicalIssueNote="Customer hit the 4.5-minute custom-case build delay notice. Please let them know when their case is ready."
                  accessToken={accessToken}
                  orderStatus={orderStatus}
                  generatedCaseId={generatedCaseId}
                  generationRunId={generationRunId}
                  elapsedLabel={elapsedLabel}
                  caseTitle={caseTitle}
                  city={city}
                  suspectNames={visibleSuspects}
                  headline={delayCopy.notifyHeadline}
                  description={delayCopy.notifyDescription}
                  successMessage={delayCopy.notifySuccessMessage}
                  notePlaceholder={delayCopy.notifyNotePlaceholder}
                  firstNameLabel={delayCopy.firstNameLabel}
                  firstNamePlaceholder={delayCopy.firstNamePlaceholder}
                  emailLabel={delayCopy.emailLabel}
                  emailPlaceholder={delayCopy.emailPlaceholder}
                  messageLabel={delayCopy.messageLabel}
                  submitIdleLabel={delayCopy.notifyIdleLabel}
                  submitBusyLabel={delayCopy.notifyBusyLabel}
                />
              </div>
            </div>
          ) : null}
          <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-4">
            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                {[0, 1, 2].map((dot) => (
                  <span
                    key={dot}
                    className={`block h-2.5 w-2.5 rounded-full ${
                      isCompleted
                        ? "bg-emerald-300"
                        : isFailed
                          ? "bg-amber-300"
                          : "bg-crimson"
                    } ${isCompleted || isFailed ? "" : "animate-pulse"}`}
                    style={
                      isCompleted || isFailed
                        ? undefined
                        : { animationDelay: `${dot * 180}ms` }
                    }
                  />
                ))}
              </div>
              <p className="text-sm font-semibold text-white">
                {isCompleted
                  ? visualCopy.pipelineFinished
                  : isFailed
                    ? visualCopy.buildStoppedBeforePersist
                    : isReviewRequired
                      ? visualCopy.qualityReviewInProgress
                      : visualCopy.storyboardInMotion}
              </p>
            </div>

            <div className="mt-5 rounded-[22px] border border-white/10 bg-[#06080d] p-4">
              <div className="mb-3 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.2em] text-white/42">
                <span>{visualCopy.assemblyFramesUpdating}</span>
                <span>
                  {isCompleted
                    ? visualCopy.locked
                    : isFailed
                      ? visualCopy.interrupted
                      : isReviewRequired
                        ? visualCopy.locked
                        : visualCopy.live}
                </span>
              </div>
              <div className="relative overflow-hidden rounded-[20px] border border-white/6 bg-[radial-gradient(circle_at_top,rgba(220,38,38,0.08),transparent_42%)] p-4">
                {isAnimated ? (
                  <div
                    className="pointer-events-none absolute inset-y-5 left-[-30%] w-[40%] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)]"
                    style={{
                      animation: "case-build-rail-sweep 3.2s linear infinite",
                    }}
                  />
                ) : null}
                <div className="flex gap-3 overflow-hidden">
                  {[0, 1, 2, 3].map((frame) => (
                    <div
                      key={frame}
                      className="relative min-h-[180px] flex-1 rounded-[18px] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))] p-3"
                      style={
                        isCompleted || isFailed || isReviewRequired
                          ? undefined
                          : {
                              animation:
                                "case-build-card-drift 2.2s ease-in-out infinite",
                              animationDelay: `${frame * 180}ms`,
                            }
                      }
                    >
                      <div className="absolute inset-x-3 top-3 h-5 rounded-full border border-white/6 bg-white/[0.03]" />
                      <div className="absolute left-3 right-3 top-12 h-[2px] overflow-hidden bg-white/8">
                        {isAnimated ? (
                          <div
                            className="h-full w-1/3 bg-[linear-gradient(90deg,transparent,rgba(94,214,181,0.85),transparent)]"
                            style={{
                              animation:
                                "case-build-scanline 1.8s linear infinite",
                              animationDelay: `${frame * 160}ms`,
                            }}
                          />
                        ) : null}
                      </div>
                      <div
                        className={`absolute left-3 top-[58px] h-[86px] rounded-[14px] border ${
                          frame % 2 === 0
                            ? "border-[#d6b26b]/25 bg-[#2c1c20]/35"
                            : "border-[#56d2b5]/18 bg-[#102028]/35"
                        }`}
                        style={{
                          right: "14px",
                          transform: isAnimated
                            ? `translateY(${frame % 2 === 0 ? 0 : 6}px)`
                            : undefined,
                        }}
                      />
                      <div className="absolute bottom-4 left-3 right-3 space-y-2">
                        <div className="h-2 rounded-full bg-white/8" />
                        <div className="h-2 w-4/5 rounded-full bg-white/8" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 h-[5px] overflow-hidden rounded-full bg-white/6">
                  <div
                    className={`h-full rounded-full ${
                      isCompleted
                        ? "bg-emerald-300/60"
                        : isFailed
                          ? "bg-amber-300/50"
                          : "bg-[linear-gradient(90deg,rgba(220,38,38,0.6),rgba(245,158,11,0.75),rgba(94,214,181,0.8))]"
                    }`}
                    style={{ width: `${progressEstimate.percent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {[
              {
                label: panelCopy.milestones.conceptLocked,
                active: true,
              },
              {
                label: panelCopy.milestones.fullCaseAssembled,
                active: [
                  "generation_running",
                  "review_required",
                  "delivery_ready",
                  "delivered",
                ].includes(orderStatus),
              },
              {
                label: panelCopy.milestones.deliveryReleaseReady,
                active: isCompleted,
              },
            ].map((step) => (
              <div key={step.label} className="flex items-center gap-3">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold ${
                    step.active
                      ? isCompleted
                        ? "border-emerald-300/35 bg-emerald-500/15 text-emerald-100"
                        : "border-crimson/35 bg-crimson/15 text-white"
                      : "border-white/10 bg-white/5 text-white/45"
                  }`}
                >
                  {step.active ? <Check className="h-3.5 w-3.5" /> : "·"}
                </span>
                <span
                  className={`${step.active ? "text-white" : "text-white/55"} text-sm`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 space-y-2 rounded-2xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
            {generatedCaseId ? (
              <div>
                <p className="uppercase tracking-[0.16em] text-white/45">
                  {panelCopy.caseIdLabel}
                </p>
                <p className="mt-1 break-all font-mono text-[11px] text-white/80">
                  {generatedCaseId}
                </p>
              </div>
            ) : null}
            {generationRunId ? (
              <div>
                <p className="uppercase tracking-[0.16em] text-white/45">
                  {panelCopy.runIdLabel}
                </p>
                <p className="mt-1 break-all font-mono text-[11px] text-white/80">
                  {generationRunId}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomCaseIntakePage = () => {
  const { accessToken = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CustomCaseFormData>(
    getDefaultCustomCaseFormData,
  );
  const [errors, setErrors] = useState<CustomCaseFormErrors>({});
  const [status, setStatus] = useState("");
  const [orderId, setOrderId] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [paymentConfirmedAt, setPaymentConfirmedAt] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectingIdea, setIsSelectingIdea] = useState(false);
  const [isRestartingFailedBuild, setIsRestartingFailedBuild] = useState(false);
  const [isRequestingMoreIdeas, setIsRequestingMoreIdeas] = useState(false);
  const [error, setError] = useState("");
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [generatedIdeas, setGeneratedIdeas] = useState<CustomOrderIdea[]>([]);
  const [selectedIdeaId, setSelectedIdeaId] = useState("");
  const [selectedIdeaAt, setSelectedIdeaAt] = useState("");
  const [queuedCaseId, setQueuedCaseId] = useState("");
  const [generationRunId, setGenerationRunId] = useState("");
  const [isConfirmingDevPayment, setIsConfirmingDevPayment] = useState(false);
  const [isPollingOrder, setIsPollingOrder] = useState(false);
  const [deliveryUrl, setDeliveryUrl] = useState("");
  const [ownerVerified, setOwnerVerified] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [rawOrderDraft, setRawOrderDraft] = useState<Record<string, unknown>>(
    {},
  );
  const hasRetriedProtectedOrderLoadRef = useRef(false);
  const showDevPaymentBypass = import.meta.env.DEV;
  const showDevSuspectPresets = import.meta.env.DEV;
  const queryLandingContext = useMemo(
    () => normalizeCustomLandingContext(new URLSearchParams(location.search)),
    [location.search],
  );
  const effectiveLandingContext = useMemo(() => {
    if (hasCustomLandingContext(queryLandingContext)) {
      return queryLandingContext;
    }
    return extractCustomLandingContextFromDraft(rawOrderDraft);
  }, [queryLandingContext, rawOrderDraft]);
  const intakeCopy = useMemo(
    () => INTAKE_PAGE_COPY[effectiveLandingContext.lang] || INTAKE_PAGE_COPY.en,
    [effectiveLandingContext.lang],
  );
  const formCopy = useMemo(
    () => buildLocalizedCustomCaseFormCopy(effectiveLandingContext.lang),
    [effectiveLandingContext.lang],
  );
  const localizedErrors = useMemo(
    () => localizeCustomCaseFormErrors(errors, effectiveLandingContext.lang),
    [errors, effectiveLandingContext.lang],
  );
  const resolveApiErrorMessage = (error: any, fallback: string) =>
    getLocalizedCustomCaseApiMessage({
      error,
      locale: effectiveLandingContext.lang,
      fallback,
    });

  const isPaymentConfirmed = Boolean(paymentConfirmedAt);
  const activeStep = INTAKE_STEPS[activeStepIndex];
  const isIdeasGenerating = orderStatus === "ideas_generating";
  const hasGeneratedIdeas = generatedIdeas.length > 0;
  const remainingIdeaRequestCount = Math.max(
    0,
    Math.floor(
      (CUSTOM_IDEA_MAX_COUNT - generatedIdeas.length) / CUSTOM_IDEA_BATCH_SIZE,
    ),
  );
  const hasQueuedGeneration = [
    "generation_queued",
    "generation_running",
    "generation_failed",
    "review_required",
    "delivery_ready",
    "delivered",
  ].includes(orderStatus);
  const shouldPollOrder =
    isIdeasGenerating || ACTIVE_BUILD_STATUSES.includes(orderStatus as any);
  const isFormLockedForGeneration = isIdeasGenerating || hasQueuedGeneration;
  const canRequestMoreIdeas =
    hasGeneratedIdeas &&
    !hasQueuedGeneration &&
    !isIdeasGenerating &&
    !selectedIdeaId &&
    remainingIdeaRequestCount > 0;
  const selectedIdea =
    generatedIdeas.find((idea) => idea.idea_id === selectedIdeaId) || null;
  const suspectNames = useMemo(
    () =>
      formData.suspects
        .map((suspect) => String(suspect?.fullName || "").trim())
        .filter(Boolean),
    [formData.suspects],
  );
  const statusSummary = useMemo(() => {
    if (isPaymentConfirmed) {
      if (COMPLETED_BUILD_STATUSES.includes(orderStatus as any)) {
        return intakeCopy.statusSummaryComplete;
      }
      if (orderStatus === "generation_failed") {
        return intakeCopy.statusSummaryFailed;
      }
      if (orderStatus === "review_required") {
        return intakeCopy.statusSummaryReviewRequired;
      }
      if (ACTIVE_BUILD_STATUSES.includes(orderStatus as any)) {
        return intakeCopy.statusSummaryQueued;
      }
      if (hasGeneratedIdeas) {
        return intakeCopy.statusSummaryChooseIdea;
      }
      if (isIdeasGenerating) {
        return intakeCopy.statusSummaryIdeasGenerating;
      }
      return intakeCopy.statusSummaryReady;
    }
    return intakeCopy.statusSummaryPending;
  }, [
    hasGeneratedIdeas,
    intakeCopy,
    isIdeasGenerating,
    isPaymentConfirmed,
    orderStatus,
  ]);

  const syncOrderState = (order: any, source: string) => {
    setOrderId(String(order.id || ""));
    setFormData(normalizeOrderDraftToFormData(order.intakeDraft));
    setRawOrderDraft(
      order?.intakeDraft && typeof order.intakeDraft === "object"
        ? order.intakeDraft
        : {},
    );
    setOrderStatus(String(order.status || ""));
    setPaymentConfirmedAt(String(order.paymentConfirmedAt || ""));
    setGeneratedIdeas(normalizeGeneratedIdeas(order.generatedIdeas));
    setSelectedIdeaId(String(order.selectedIdea?.idea_id || ""));
    setSelectedIdeaAt(String(order.selectedIdeaAt || ""));
    setQueuedCaseId(String(order.generatedCaseId || ""));
    setGenerationRunId(String(order.generationRunId || ""));

    devLog("route", "custom_case_intake_order_state_synced", {
      route: "/custom/intake/:accessToken",
      source,
      status: String(order.status || ""),
      paymentConfirmed: Boolean(order.paymentConfirmedAt),
      ideaCount: Array.isArray(order.generatedIdeas)
        ? order.generatedIdeas.length
        : 0,
      hasSelectedIdea: Boolean(order.selectedIdea?.idea_id),
      selectedIdeaAt: String(order.selectedIdeaAt || ""),
      generatedCaseId: String(order.generatedCaseId || ""),
    });

    if (String(order.status || "") === "generation_failed") {
      devWarn("route", "custom_case_intake_generation_failed_state_seen", {
        route: "/custom/intake/:accessToken",
        source,
        status: String(order.status || ""),
        generatedCaseId: String(order.generatedCaseId || ""),
        selectedIdeaId: String(order.selectedIdea?.idea_id || ""),
      });
    }
  };

  const applyFormDataUpdate = (
    updater: (current: CustomCaseFormData) => CustomCaseFormData,
  ) => {
    setFormData((current) => {
      const next = updater(current);
      setErrors((currentErrors) => {
        if (Object.keys(currentErrors).length === 0) return currentErrors;
        return validateCustomCaseForm(next);
      });
      return next;
    });
  };

  const buildStepErrors = (stepKey: (typeof INTAKE_STEPS)[number]["key"]) => {
    const nextErrors = validateCustomCaseForm(formData);
    if (stepKey === "details") {
      return {
        firstName: nextErrors.firstName,
        email: nextErrors.email,
        city: nextErrors.city,
        location: nextErrors.location,
        source: nextErrors.source,
      } as CustomCaseFormErrors;
    }
    if (stepKey === "suspects") {
      return {
        suspects: nextErrors.suspects,
      } as CustomCaseFormErrors;
    }
    return nextErrors;
  };

  const handleAdvanceStep = () => {
    if (activeStep.key === "review") return;

    const nextErrors = buildStepErrors(activeStep.key);
    setErrors(nextErrors);
    const hasBlockingIssues =
      activeStep.key === "details"
        ? Boolean(
            nextErrors.firstName ||
              nextErrors.email ||
              nextErrors.city ||
              nextErrors.location ||
              nextErrors.source,
          )
        : Array.isArray(nextErrors.suspects) && nextErrors.suspects.length > 0;

    if (hasBlockingIssues) {
      devWarn("route", "custom_case_intake_step_validation_failed", {
        route: "/custom/intake/:accessToken",
        step: activeStep.key,
        nextStep:
          INTAKE_STEPS[Math.min(activeStepIndex + 1, INTAKE_STEPS.length - 1)]
            .key,
        hasFirstNameError: Boolean(nextErrors.firstName),
        hasEmailError: Boolean(nextErrors.email),
        hasCityError: Boolean(nextErrors.city),
        hasLocationError: Boolean(nextErrors.location),
        hasSourceError: Boolean(nextErrors.source),
        suspectIssueCount: Array.isArray(nextErrors.suspects)
          ? nextErrors.suspects.length
          : 0,
      });
      return;
    }

    const nextIndex = Math.min(activeStepIndex + 1, INTAKE_STEPS.length - 1);
    setErrors({});
    setActiveStepIndex(nextIndex);
    devLog("route", "custom_case_intake_step_advanced", {
      route: "/custom/intake/:accessToken",
      fromStep: activeStep.key,
      toStep: INTAKE_STEPS[nextIndex].key,
    });
  };

  const handleReturnStep = () => {
    const nextIndex = Math.max(0, activeStepIndex - 1);
    setActiveStepIndex(nextIndex);
    setErrors({});
    devLog("route", "custom_case_intake_step_returned", {
      route: "/custom/intake/:accessToken",
      fromStep: activeStep.key,
      toStep: INTAKE_STEPS[nextIndex].key,
    });
  };

  const loadOrder = async (
    source: string,
    options: { silent?: boolean } = {},
  ) => {
    if (!accessToken) return;
    const { silent = false } = options;
    try {
      if (!silent) {
        setIsLoading(true);
      } else {
        setIsPollingOrder(true);
      }
      setError("");
      devLog("route", "custom_case_intake_load_started", {
        route: "/custom/intake/:accessToken",
        source,
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.getOrder(accessToken);
      const order = response.order;
      if (order.paymentConfirmedAt) {
        saveLastCustomOrderAccessToken(order.accessToken);
        clearPendingCustomOrderAccessToken();
      }
      if (order.customerEmail) {
        saveLastCustomOrderEmail(order.customerEmail);
        cartService.saveCustomerEmail(order.customerEmail);
      }
      syncOrderState(order, source);
      setStatus(resolveOrderStatusMessage(order, intakeCopy));
      devLog("route", "custom_case_intake_load_succeeded", {
        route: "/custom/intake/:accessToken",
        source,
        status: order.status,
        paymentConfirmed: Boolean(order.paymentConfirmedAt),
        suspectCount: Number(order.suspectCount || 0),
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to load your custom order.",
      );
      if (
        ownerVerified &&
        !silent &&
        !hasRetriedProtectedOrderLoadRef.current
      ) {
        hasRetriedProtectedOrderLoadRef.current = true;
        devWarn("route", "custom_case_intake_load_retry_scheduled", {
          route: "/custom/intake/:accessToken",
          source,
          status: err?.response?.status,
          message: nextMessage,
        });
        window.setTimeout(() => {
          void loadOrder("owner_access_verified_retry");
        }, 300);
      }
      setError(nextMessage);
      devError("route", "custom_case_intake_load_failed", {
        route: "/custom/intake/:accessToken",
        source,
        status: err?.response?.status,
        message: nextMessage,
      });
    } finally {
      if (!silent) {
        setIsLoading(false);
      } else {
        setIsPollingOrder(false);
      }
    }
  };

  const loadDelivery = async (source: string) => {
    if (!accessToken) return;
    try {
      devLog("route", "custom_case_delivery_lookup_started", {
        route: "/custom/intake/:accessToken",
        source,
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.getDelivery(accessToken);
      const nextUrl = String(
        response?.delivery?.caseAccessUrl ||
          response?.delivery?.packetPdfUrl ||
          "",
      ).trim();
      setDeliveryUrl(nextUrl);
      devLog("route", "custom_case_delivery_lookup_succeeded", {
        route: "/custom/intake/:accessToken",
        source,
        available: Boolean(response?.delivery?.available),
        hasDeliveryUrl: Boolean(nextUrl),
        usedCaseAccessUrl: Boolean(response?.delivery?.caseAccessUrl),
      });
    } catch (err: any) {
      devError("route", "custom_case_delivery_lookup_failed", {
        route: "/custom/intake/:accessToken",
        source,
        status: err?.response?.status,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "delivery_lookup_failed",
      });
    }
  };

  useEffect(() => {
    if (!accessToken) {
      navigate("/custom", { replace: true });
      return;
    }

    devLog("route", "custom_case_intake_route_rendered", {
      route: "/custom/intake/:accessToken",
      hasAccessToken: Boolean(accessToken),
    });
  }, [accessToken, navigate]);

  useEffect(() => {
    if (generatedIdeas.length === 0) return;
    devLog("route", "custom_case_intake_ideas_rendered", {
      route: "/custom/intake/:accessToken",
      ideaCount: generatedIdeas.length,
      shellLabels: generatedIdeas
        .map((idea) => idea.shellLabel || idea.title)
        .filter(Boolean),
    });
  }, [generatedIdeas]);

  useEffect(() => {
    hasRetriedProtectedOrderLoadRef.current = false;
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || !ownerVerified) return;
    void loadOrder("owner_access_verified");
  }, [accessToken, ownerVerified]);

  useEffect(() => {
    const handleFastSpringClose = (event: Event) => {
      if (!accessToken) return;
      const detail = ((event as CustomEvent<FastSpringPopupClosePayload>)
        .detail || null) as FastSpringPopupClosePayload;

      if (isFastSpringPopupPurchaseComplete(detail)) {
        setStatus(intakeCopy.statusCheckoutRefreshAfterPayment);
        devLog("route", "custom_case_checkout_popup_completed", {
          route: "/custom/intake/:accessToken",
          accessToken,
          fastspringOrderId: String(detail?.id || ""),
          fastspringOrderReference: String(detail?.reference || ""),
        });
        void loadOrder("fastspring_popup_completed");
        return;
      }

      clearPendingCustomOrderAccessToken();
      setStatus(intakeCopy.statusCheckoutClosedBeforePayment);
      devLog("route", "custom_case_checkout_cancelled_before_payment", {
        route: "/custom/intake/:accessToken",
        accessToken,
      });
    };

    window.addEventListener(
      "fastspring:close",
      handleFastSpringClose as EventListener,
    );
    return () => {
      window.removeEventListener(
        "fastspring:close",
        handleFastSpringClose as EventListener,
      );
    };
  }, [accessToken]);

  useEffect(() => {
    storeCustomCaseFormData(formData);
  }, [formData]);

  useEffect(() => {
    if (!isPaymentConfirmed) return;
    devLog("route", "custom_case_intake_step_viewed", {
      route: "/custom/intake/:accessToken",
      step: activeStep.key,
      stepIndex: activeStepIndex,
    });
  }, [activeStep.key, activeStepIndex, isPaymentConfirmed]);

  useEffect(() => {
    if (!accessToken || !shouldPollOrder) return;

    devLog("route", "custom_case_intake_poll_started", {
      route: "/custom/intake/:accessToken",
      status: orderStatus,
    });

    const intervalId = window.setInterval(() => {
      void loadOrder("status_poll", { silent: true });
    }, 7000);

    return () => {
      window.clearInterval(intervalId);
      devLog("route", "custom_case_intake_poll_stopped", {
        route: "/custom/intake/:accessToken",
        status: orderStatus,
      });
    };
  }, [accessToken, orderStatus, shouldPollOrder]);

  useEffect(() => {
    if (!accessToken || !COMPLETED_BUILD_STATUSES.includes(orderStatus as any))
      return;
    void loadDelivery("completed_status");
  }, [accessToken, orderStatus]);

  useEffect(() => {
    if (!accessToken || !ownerVerified) return;
    if (!COMPLETED_BUILD_STATUSES.includes(orderStatus as any)) return;

    const targetSearch = location.search || "";
    devLog("route", "custom_case_intake_redirected_to_case_access", {
      route: "/custom/intake/:accessToken",
      status: orderStatus,
      generatedCaseId: queuedCaseId,
      targetSearch,
    });
    navigate(`/custom/case/${accessToken}${targetSearch}`, { replace: true });
  }, [
    accessToken,
    location.search,
    navigate,
    orderStatus,
    ownerVerified,
    queuedCaseId,
  ]);

  const handleAddSuspect = () => {
    if (formData.suspects.length >= 6) return;
    const lastSuspect = formData.suspects.at(-1);
    if (lastSuspect && !isCustomCaseSuspectComplete(lastSuspect)) {
      const missingFields = formatMissingSuspectFields(
        getIncompleteSuspectFields(lastSuspect),
        effectiveLandingContext.lang,
      );
      setStatus(
        replaceTemplateTokens(intakeCopy.finishSuspectBeforeAddingWithMissing, {
          index: formData.suspects.length,
          fields: missingFields.join(", "),
        }),
      );
      devWarn(
        "route",
        "custom_case_intake_add_suspect_blocked_incomplete_previous",
        {
          route: "/custom/intake/:accessToken",
          suspectIndex: formData.suspects.length - 1,
          missingFields,
        },
      );
      return;
    }
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: [...current.suspects, getEmptySuspect()],
    }));
  };

  const handleRemoveSuspect = (index: number) => {
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: current.suspects.filter(
        (_, suspectIndex) => suspectIndex !== index,
      ),
    }));
  };

  const handleSuspectChange = (
    index: number,
    field: keyof (typeof formData.suspects)[number],
    value: string,
  ) => {
    applyFormDataUpdate((current) => ({
      ...current,
      suspects: current.suspects.map((suspect, suspectIndex) =>
        suspectIndex === index ? { ...suspect, [field]: value } : suspect,
      ),
    }));
  };

  const handleApplyDevSuspectPreset = (presetKey: string) => {
    const presetSuspects = DEV_SUSPECT_PRESETS[presetKey];
    if (
      !showDevSuspectPresets ||
      !Array.isArray(presetSuspects) ||
      presetSuspects.length === 0
    ) {
      devWarn("route", "custom_case_intake_dev_suspect_preset_missing", {
        route: "/custom/intake/:accessToken",
        presetKey,
      });
      return;
    }

    applyFormDataUpdate((current) => ({
      ...current,
      suspects: presetSuspects.map((suspect) => ({
        ...getEmptySuspect(),
        ...suspect,
      })),
    }));
    setStatus(
      replaceTemplateTokens(intakeCopy.devPresetLoaded, {
        preset: presetKey,
      }),
    );
    devLog("route", "custom_case_intake_dev_suspect_preset_applied", {
      route: "/custom/intake/:accessToken",
      presetKey,
      suspectCount: presetSuspects.length,
      suspectNames: presetSuspects.map((suspect) => suspect.fullName),
    });
  };

  const handleSaveDraft = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!accessToken) return;

    try {
      setIsSaving(true);
      setError("");
      devLog("route", "custom_case_intake_save_started", {
        route: "/custom/intake/:accessToken",
        status: orderStatus,
        suspectCount: formData.suspects.length,
      });
      const response = await CustomOrderAPI.saveIntakeDraft(
        accessToken,
        formData as unknown as Record<string, unknown>,
      );
      syncOrderState(response.order, "save_draft");
      setStatus(intakeCopy.statusDraftSaved);
      devLog("route", "custom_case_intake_save_succeeded", {
        route: "/custom/intake/:accessToken",
        status: response.order.status,
        paymentConfirmed: Boolean(response.order.paymentConfirmedAt),
        suspectCount: Number(response.order.suspectCount || 0),
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to save your custom-case draft.",
      );
      setError(nextMessage);
      devError("route", "custom_case_intake_save_failed", {
        route: "/custom/intake/:accessToken",
        status: err?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitIntake = async () => {
    if (!accessToken) return;
    const nextErrors = validateCustomCaseForm(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const redirectStepIndex =
        nextErrors.firstName ||
        nextErrors.email ||
        nextErrors.city ||
        nextErrors.source
          ? 0
          : 1;
      setActiveStepIndex(redirectStepIndex);
      setStatus(intakeCopy.statusValidationMissing);
      devWarn("route", "custom_case_intake_validation_failed", {
        route: "/custom/intake/:accessToken",
        issueCount: Array.isArray(nextErrors.suspects)
          ? nextErrors.suspects.length
          : 0,
        hasFirstNameError: Boolean(nextErrors.firstName),
        hasEmailError: Boolean(nextErrors.email),
        hasCityError: Boolean(nextErrors.city),
        redirectStep: INTAKE_STEPS[redirectStepIndex].key,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      devLog("route", "custom_case_intake_submit_started", {
        route: "/custom/intake/:accessToken",
        suspectCount: formData.suspects.length,
      });
      await CustomOrderAPI.saveIntakeDraft(
        accessToken,
        formData as unknown as Record<string, unknown>,
      );
      const response = await CustomOrderAPI.submitIntake(accessToken);
      syncOrderState(response.order, "submit_intake");
      setStatus(intakeCopy.statusSubmitted);
      clearStoredCustomCaseFormData();
      devLog("route", "custom_case_intake_submit_succeeded", {
        route: "/custom/intake/:accessToken",
        status: response.order.status,
        suspectCount: Number(response.order.suspectCount || 0),
        ideaCount: Array.isArray(response.order.generatedIdeas)
          ? response.order.generatedIdeas.length
          : 0,
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to submit your custom-case intake.",
      );
      setError(nextMessage);
      const issueMap = Array.isArray(err?.response?.data?.issues)
        ? err.response.data.issues
        : [];
      setErrors((current) =>
        localizeCustomCaseFormErrors(
          {
            ...current,
            firstName:
              issueMap.find(
                (issue: any) => String(issue?.field || "") === "firstName",
              )?.message || current.firstName,
            email:
              issueMap.find(
                (issue: any) => String(issue?.field || "") === "email",
              )?.message || current.email,
            city:
              issueMap.find((issue: any) => String(issue?.field || "") === "city")
                ?.message || current.city,
            suspects: issueMap
              .filter((issue: any) =>
                String(issue?.field || "").startsWith("suspects."),
              )
              .map((issue: any) => ({
                index: Number(String(issue.field).split(".")[1] || 0),
                field: String(issue.field).split(".")[2] || "general",
                message: String(issue.message || "Invalid suspect field"),
              })),
          },
          effectiveLandingContext.lang,
        ),
      );
      devError("route", "custom_case_intake_submit_failed", {
        route: "/custom/intake/:accessToken",
        status: err?.response?.status,
        message: nextMessage,
        issues: err?.response?.data?.issues,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectIdea = async (ideaId: string) => {
    if (!accessToken || !ideaId) return;

    try {
      setIsSelectingIdea(true);
      setError("");
      devLog("route", "custom_case_intake_idea_select_started", {
        route: "/custom/intake/:accessToken",
        ideaId,
        currentStatus: orderStatus,
      });
      const response = await CustomOrderAPI.selectIdea(accessToken, ideaId);
      syncOrderState(response.order, "select_idea");
      setStatus(
        response.queue?.webhookTriggered
          ? intakeCopy.statusIdeaQueued
          : intakeCopy.statusIdeaAccepted,
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      devLog("route", "custom_case_intake_idea_select_succeeded", {
        route: "/custom/intake/:accessToken",
        ideaId,
        status: response.order.status,
        generatedCaseId: response.order.generatedCaseId,
        generationRunId: response.order.generationRunId,
        webhookTriggered: Boolean(response.queue?.webhookTriggered),
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to queue this concept.",
      );
      setError(nextMessage);
      devError("route", "custom_case_intake_idea_select_failed", {
        route: "/custom/intake/:accessToken",
        ideaId,
        status: err?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsSelectingIdea(false);
    }
  };

  const handleRequestMoreIdeas = async () => {
    if (!accessToken || !canRequestMoreIdeas) return;

    try {
      setIsRequestingMoreIdeas(true);
      setError("");
      devLog("route", "custom_case_intake_more_ideas_started", {
        route: "/custom/intake/:accessToken",
        currentIdeaCount: generatedIdeas.length,
        remainingIdeaRequestCount,
      });
      const response = await CustomOrderAPI.requestMoreIdeas(accessToken);
      syncOrderState(response.order, "request_more_ideas");
      const nextIdeaCount = Array.isArray(response.order.generatedIdeas)
        ? response.order.generatedIdeas.length
        : 0;
      const nextRemainingIdeaRequestCount = Math.max(
        0,
        Math.floor(
          (CUSTOM_IDEA_MAX_COUNT - nextIdeaCount) / CUSTOM_IDEA_BATCH_SIZE,
        ),
      );
      setStatus(
        replaceCountToken(
          nextRemainingIdeaRequestCount > 0
            ? intakeCopy.statusMoreIdeas
            : intakeCopy.statusFinalIdeas,
          nextIdeaCount,
        ),
      );
      devLog("route", "custom_case_intake_more_ideas_succeeded", {
        route: "/custom/intake/:accessToken",
        ideaCount: nextIdeaCount,
        remainingIdeaRequestCount: nextRemainingIdeaRequestCount,
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to generate three more case directions right now.",
      );
      setError(nextMessage);
      devError("route", "custom_case_intake_more_ideas_failed", {
        route: "/custom/intake/:accessToken",
        status: err?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsRequestingMoreIdeas(false);
    }
  };

  const handleReopenCheckout = () => {
    savePendingCustomOrderAccessToken(accessToken);
    const checkout = launchCustomCheckout({
      email: formData.email || undefined,
      customOrderId: orderId || undefined,
      accessToken: accessToken || undefined,
      landingContext: effectiveLandingContext,
    });
    if (!checkout.launched) {
      clearPendingCustomOrderAccessToken();
      setError(intakeCopy.checkoutUnavailable);
      devWarn("route", "custom_case_intake_checkout_relaunch_unavailable", {
        route: "/custom/intake/:accessToken",
        hasEmail: Boolean(formData.email),
      });
      return;
    }

    devLog("route", "custom_case_intake_checkout_relaunch_succeeded", {
      route: "/custom/intake/:accessToken",
      method: checkout.method,
      correlation: checkout.correlation,
      hasEmail: Boolean(formData.email),
      hasOrderId: Boolean(orderId),
    });
  };

  const handleDevConfirmPayment = async () => {
    if (!accessToken || !showDevPaymentBypass) return;

    try {
      setIsConfirmingDevPayment(true);
      setError("");
      devLog("route", "custom_case_intake_dev_confirm_payment_started", {
        route: "/custom/intake/:accessToken",
        hasAccessToken: Boolean(accessToken),
      });
      const response = await CustomOrderAPI.confirmDevPayment(accessToken);
      saveLastCustomOrderAccessToken(response.order.accessToken);
      clearPendingCustomOrderAccessToken();
      syncOrderState(response.order, "dev_confirm_payment");
      setStatus(intakeCopy.statusDevBypass);
      devLog("route", "custom_case_intake_dev_confirm_payment_succeeded", {
        route: "/custom/intake/:accessToken",
        status: response.order.status,
        paymentConfirmed: Boolean(response.order.paymentConfirmedAt),
      });
    } catch (err: any) {
      const nextMessage = resolveApiErrorMessage(
        err,
        "Unable to bypass payment in dev.",
      );
      setError(nextMessage);
      devError("route", "custom_case_intake_dev_confirm_payment_failed", {
        route: "/custom/intake/:accessToken",
        status: err?.response?.status,
        message: nextMessage,
      });
    } finally {
      setIsConfirmingDevPayment(false);
    }
  };

  const handleStartFreshCase = async () => {
    if (orderStatus === "generation_failed") {
      try {
        setIsRestartingFailedBuild(true);
        setError("");
        setStatus(intakeCopy.statusRestartingBuild);
        devLog("route", "custom_case_build_restart_started", {
          route: "/custom/intake/:accessToken",
          accessToken,
          previousStatus: orderStatus,
          generatedCaseId: queuedCaseId,
          generationRunId,
        });
        const response = await CustomOrderAPI.restartGeneration(accessToken);
        syncOrderState(response.order, "restart_generation");
        setDeliveryUrl("");
        setStatus(
          response.queue?.webhookTriggered
            ? intakeCopy.statusBuildRestartQueued
            : intakeCopy.statusBuildRestartAccepted,
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        devLog("route", "custom_case_build_restart_succeeded", {
          route: "/custom/intake/:accessToken",
          accessToken,
          status: response.order.status,
          generatedCaseId: response.order.generatedCaseId,
          generationRunId: response.order.generationRunId,
          webhookTriggered: Boolean(response.queue?.webhookTriggered),
        });
        return;
      } catch (err: any) {
        const nextMessage = resolveApiErrorMessage(
          err,
          "Unable to restart this custom-case build right now.",
        );
        setError(nextMessage);
        devError("route", "custom_case_build_restart_failed", {
          route: "/custom/intake/:accessToken",
          accessToken,
          status: err?.response?.status,
          message: nextMessage,
        });
        return;
      } finally {
        setIsRestartingFailedBuild(false);
      }
    }

    clearPendingCustomOrderAccessToken();
    clearLastCustomOrderAccessToken();
    clearLastCustomOrderEmail();
    clearStoredCustomCaseFormData();
    setDeliveryUrl("");
    devLog("route", "custom_case_build_start_fresh_requested", {
      route: "/custom/intake/:accessToken",
      previousStatus: orderStatus,
      hadDeliveryUrl: Boolean(deliveryUrl),
    });
    navigate("/custom", { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>{intakeCopy.helmetTitle}</title>
        <meta name="description" content={intakeCopy.helmetDescription} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <CustomOrderOwnerAccessGate
          accessToken={accessToken}
          routeLabel="/custom/intake/:accessToken"
          locale={effectiveLandingContext.lang}
          title={intakeCopy.ownerAccessTitle}
          description={intakeCopy.ownerAccessDescription}
          copy={intakeCopy.ownerAccessCopy}
          onVerified={() => setOwnerVerified(true)}
        >
          <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-card/80 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.22)]">
            <p className="text-xs uppercase tracking-[0.24em] text-crimson">
              {intakeCopy.eyebrow}
            </p>
            <h1
              className={`font-heading spotlight-text ${CASE_VIEW_PAGE_TITLE_CLASS}`}
            >
              {intakeCopy.title}
            </h1>
            <p
              className={`mt-3 text-muted-foreground ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
            >
              {statusSummary}
            </p>

            {error ? (
              <div className="mt-4 rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
                {error}
              </div>
            ) : null}

            <div className="mt-5">
              <button
                type="button"
                onClick={() => setShowOrderDetails((current) => !current)}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                <span>
                  {showOrderDetails
                    ? intakeCopy.hideOrderDetails
                    : intakeCopy.showOrderDetails}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition ${showOrderDetails ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            {showOrderDetails ? (
              <div className="mt-5 space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-border bg-muted/30 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {intakeCopy.orderStatusLabel}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-foreground">
                      {orderStatus || intakeCopy.orderStatusLoading}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/30 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {intakeCopy.paymentLabel}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-foreground">
                      {paymentConfirmedAt
                        ? intakeCopy.paymentConfirmedLabel
                        : intakeCopy.paymentPendingLabel}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/30 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {intakeCopy.accessTokenLabel}
                    </p>
                    <p className="mt-2 break-all text-xs text-muted-foreground">
                      {accessToken}
                    </p>
                  </div>
                </div>

                {status ? (
                  <div className="rounded-2xl border border-crimson/20 bg-crimson/5 p-4 text-sm text-foreground">
                    {status}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => loadOrder("manual_retry")}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4" />
                    )}
                    {intakeCopy.checkPaymentAgain}
                  </button>
                  {!isPaymentConfirmed ? (
                    <button
                      type="button"
                      onClick={handleReopenCheckout}
                      className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                      <Clock3 className="h-4 w-4" />
                      {intakeCopy.reopenCheckout}
                    </button>
                  ) : null}
                  {!isPaymentConfirmed && showDevPaymentBypass ? (
                    <button
                      type="button"
                      onClick={handleDevConfirmPayment}
                      disabled={isConfirmingDevPayment}
                      className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/16 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isConfirmingDevPayment ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {isConfirmingDevPayment
                        ? intakeCopy.confirmingDevPayment
                        : intakeCopy.bypassPaymentInDev}
                    </button>
                  ) : null}
                  <Link
                    to="/custom"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
                  >
                    {intakeCopy.backToCustomCases}
                  </Link>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-6 rounded-3xl border border-border bg-card/60 p-6">
            {isLoading ? (
              <div className="flex min-h-48 items-center justify-center text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {intakeCopy.loadingOrder}
              </div>
            ) : isPaymentConfirmed ? (
              <>
                {hasQueuedGeneration ? (
                  <BuildActivityPanel
                    accessToken={accessToken}
                    orderStatus={orderStatus}
                    generatedCaseId={queuedCaseId}
                    generationRunId={generationRunId}
                    buildStartedAt={selectedIdeaAt || paymentConfirmedAt}
                    isPolling={isPollingOrder}
                    caseTitle={selectedIdea?.title || ""}
                    city={formData.city}
                    suspects={suspectNames}
                    deliveryUrl={deliveryUrl}
                    onStartFresh={handleStartFreshCase}
                    isRestartingFailedBuild={isRestartingFailedBuild}
                    lang={effectiveLandingContext.lang}
                  />
                ) : null}

                {!hasQueuedGeneration ? (
                  <>
                    <div className="mb-5 flex items-center gap-2 rounded-2xl border border-emerald-300/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                      <Check className="h-4 w-4" />
                      {intakeCopy.paymentBanner}
                    </div>
                    <div className="mb-6 grid gap-3 md:grid-cols-3">
                      {INTAKE_STEPS.map((step, index) => {
                        const localizedStep = intakeCopy.steps[step.key];
                        const isActive = index === activeStepIndex;
                        const isComplete = index < activeStepIndex;
                        return (
                          <button
                            key={step.key}
                            type="button"
                            onClick={() => {
                              if (index > activeStepIndex) return;
                              setActiveStepIndex(index);
                              setErrors({});
                              devLog(
                                "route",
                                "custom_case_intake_step_selected",
                                {
                                  route: "/custom/intake/:accessToken",
                                  step: step.key,
                                  stepIndex: index,
                                },
                              );
                            }}
                            disabled={index > activeStepIndex}
                            className={`rounded-2xl border px-4 py-4 text-left transition ${
                              isActive
                                ? "border-crimson bg-crimson/10"
                                : isComplete
                                  ? "border-emerald-300/20 bg-emerald-500/10"
                                  : "border-border bg-muted/20"
                            } ${index > activeStepIndex ? "cursor-not-allowed opacity-55" : ""}`}
                          >
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                              {intakeCopy.stepWord} {index + 1}
                            </p>
                            <p className="mt-2 text-base font-semibold text-foreground">
                              {localizedStep.label}
                            </p>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">
                              {localizedStep.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>

                    {activeStep.key === "review" ? (
                      <div className="space-y-5">
                        <div className="rounded-2xl border border-border bg-muted/20 p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            {intakeCopy.reviewSummary}
                          </p>
                          <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.firstName}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.firstName || intakeCopy.notProvided}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.email}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.email || intakeCopy.notProvided}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.city}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.city || intakeCopy.notProvided}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.location}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.location || intakeCopy.notSpecified}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.referralSource}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.source || intakeCopy.notProvided}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {intakeCopy.suspectsCount}
                              </p>
                              <p className="mt-1 text-sm text-foreground">
                                {formData.suspects.length}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-muted/20 p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            {intakeCopy.suspectReview}
                          </p>
                          <div className="mt-4 space-y-4">
                            {formData.suspects.map((suspect, index) => (
                              <div
                                key={`${suspect.fullName || "suspect"}-${index}`}
                                className="rounded-2xl border border-border bg-background/40 p-4"
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm font-semibold text-foreground">
                                    {suspect.fullName ||
                                      `${intakeCopy.steps.suspects.label} ${index + 1}`}
                                  </p>
                                  <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                    {intakeCopy.steps.suspects.label}{" "}
                                    {index + 1}
                                  </span>
                                </div>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                      {intakeCopy.ageSex}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">
                                      {[suspect.age, suspect.sex]
                                        .filter(Boolean)
                                        .join(" / ") || intakeCopy.notProvided}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                      {intakeCopy.hairSkin}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">
                                      {[
                                        suspect.hairColor,
                                        suspect.hairLength,
                                        suspect.skinTone,
                                      ]
                                        .filter(Boolean)
                                        .join(" / ") || intakeCopy.notProvided}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                      {intakeCopy.occupation}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">
                                      {suspect.occupation ||
                                        intakeCopy.notProvided}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                      {intakeCopy.relationship}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">
                                      {suspect.relationship ||
                                        intakeCopy.notSpecified}
                                    </p>
                                  </div>
                                  <div className="sm:col-span-2">
                                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                      {intakeCopy.notes}
                                    </p>
                                    <p className="mt-1 text-sm text-foreground">
                                      {suspect.notes || intakeCopy.noExtraNotes}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <CustomCaseFormFields
                        formData={formData}
                        errors={localizedErrors}
                        disabled={
                          isSaving || isSubmitting || isFormLockedForGeneration
                        }
                        sections={activeStep.sections}
                        copy={formCopy}
                        suspectPresetOptions={
                          showDevSuspectPresets
                            ? DEV_SUSPECT_PRESET_OPTIONS
                            : []
                        }
                        canAddSuspect={
                          formData.suspects.length === 0 ||
                          isCustomCaseSuspectComplete(
                            formData.suspects[formData.suspects.length - 1],
                          )
                        }
                        addSuspectHint={
                          formData.suspects.length > 0 &&
                          !isCustomCaseSuspectComplete(
                            formData.suspects[formData.suspects.length - 1],
                          )
                            ? replaceTemplateTokens(
                                intakeCopy.finishSuspectBeforeAdding,
                                {
                                  index: formData.suspects.length,
                                },
                              )
                            : ""
                        }
                        onFormDataChange={applyFormDataUpdate}
                        onApplySuspectPreset={
                          showDevSuspectPresets
                            ? handleApplyDevSuspectPreset
                            : undefined
                        }
                        onAddSuspect={handleAddSuspect}
                        onRemoveSuspect={handleRemoveSuspect}
                        onSuspectChange={handleSuspectChange}
                      />
                    )}

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-wrap gap-3">
                        {activeStepIndex > 0 ? (
                          <button
                            type="button"
                            onClick={handleReturnStep}
                            disabled={
                              isSaving ||
                              isSubmitting ||
                              isFormLockedForGeneration
                            }
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <ChevronLeft className="h-4 w-4" />
                            {intakeCopy.backButton}
                          </button>
                        ) : null}
                        {activeStep.key !== "review" ? (
                          <button
                            type="button"
                            onClick={handleAdvanceStep}
                            disabled={
                              isSaving ||
                              isSubmitting ||
                              isFormLockedForGeneration
                            }
                            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {intakeCopy.continueButton}
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => void handleSaveDraft()}
                          disabled={
                            isSaving ||
                            isSubmitting ||
                            isFormLockedForGeneration
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/16 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                          {isSaving
                            ? intakeCopy.savingButton
                            : intakeCopy.saveDraftButton}
                        </button>
                        {activeStep.key === "review" ? (
                          <button
                            type="button"
                            onClick={handleSubmitIntake}
                            disabled={
                              isSubmitting ||
                              isSaving ||
                              isFormLockedForGeneration ||
                              hasGeneratedIdeas
                            }
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isSubmitting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            {isSubmitting
                              ? intakeCopy.submittingButton
                              : intakeCopy.submitButton}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {isIdeasGenerating ? (
                      <div className="mt-8 rounded-3xl border border-amber-300/20 bg-amber-500/10 p-6">
                        <div className="flex items-center gap-3 text-amber-100">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <div>
                            <p className="text-sm font-semibold">
                              {intakeCopy.generatingIdeasTitle}
                            </p>
                            <p className="mt-1 text-sm text-amber-100/80">
                              {intakeCopy.generatingIdeasBody}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {hasGeneratedIdeas ? (
                      <div className="mt-8 rounded-3xl border border-border bg-card/70 p-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-[0.22em] text-crimson">
                              {intakeCopy.ideasEyebrow}
                            </p>
                            <h2
                              className={`font-heading text-foreground ${CASE_VIEW_SECTION_HEADING_CLASS.replace("text-white", "")}`}
                            >
                              {intakeCopy.ideasTitle}
                            </h2>
                            <p
                              className={`mt-2 max-w-2xl text-muted-foreground ${CASE_VIEW_SUPPORTING_READING_CLASS}`}
                            >
                              {intakeCopy.ideasBody}
                            </p>
                          </div>
                          {canRequestMoreIdeas ? (
                            <button
                              type="button"
                              onClick={() => void handleRequestMoreIdeas()}
                              disabled={
                                isRequestingMoreIdeas || isSelectingIdea
                              }
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary transition hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isRequestingMoreIdeas ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  {intakeCopy.getMoreIdeasBusy}
                                </>
                              ) : (
                                <>
                                  <Sparkles className="h-4 w-4" />
                                  {`${intakeCopy.getMoreIdeas} (${replaceCountToken(intakeCopy.getMoreIdeasRemaining, remainingIdeaRequestCount)})`}
                                </>
                              )}
                            </button>
                          ) : hasQueuedGeneration && queuedCaseId ? (
                            <div className="rounded-2xl border border-emerald-300/20 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-100">
                              <p className="font-semibold uppercase tracking-[0.18em]">
                                {intakeCopy.queuedLabel}
                              </p>
                              <p className="mt-1 break-all">{queuedCaseId}</p>
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-6 grid items-stretch gap-4 xl:grid-cols-3">
                          {generatedIdeas.map((idea, index) => {
                            const isSelected = selectedIdeaId === idea.idea_id;
                            return (
                              <div
                                key={idea.idea_id || `idea-${index}`}
                                className={`flex h-full flex-col rounded-3xl border p-5 transition ${
                                  isSelected
                                    ? "border-emerald-300/40 bg-emerald-500/10 shadow-[0_20px_50px_rgba(16,185,129,0.12)]"
                                    : "border-border bg-muted/20"
                                }`}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    {intakeCopy.optionLabel} {index + 1}
                                  </p>
                                  {isSelected ? (
                                    <span className="rounded-full border border-emerald-300/25 bg-emerald-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-100">
                                      {intakeCopy.selectedLabel}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="mt-3 flex flex-1 flex-col">
                                  <h3 className="text-[1.3rem] font-semibold font-heading leading-tight text-foreground sm:text-xl">
                                    {idea.title}
                                  </h3>
                                  <p
                                    className={`mt-3 font-medium text-foreground/95 ${CASE_VIEW_PRIMARY_READING_CLASS}`}
                                  >
                                    {buildIdeaChoiceBlurb(idea)}
                                  </p>
                                </div>
                                <div className="mt-5 flex flex-wrap gap-2">
                                  <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                    {intakeCopy.difficultyLabel} {idea.difficulty || 3}
                                  </span>
                                  {idea.shellLabel ? (
                                    <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                      {idea.shellLabel}
                                    </span>
                                  ) : null}
                                  <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                                    {localizeIdeaThemeLabel(idea.theme || "modern", intakeCopy)}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    void handleSelectIdea(idea.idea_id)
                                  }
                                  disabled={
                                    isSelectingIdea || hasQueuedGeneration
                                  }
                                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isSelectingIdea && isSelected ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      {intakeCopy.queueing}
                                    </>
                                  ) : hasQueuedGeneration && isSelected ? (
                                    <>
                                      <Check className="h-4 w-4" />
                                      {intakeCopy.queuedForBuild}
                                    </>
                                  ) : (
                                    <>
                                      <Check className="h-4 w-4" />
                                      {intakeCopy.buildThisCase}
                                    </>
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {selectedIdea && generationRunId ? (
                          <div className="mt-6 rounded-2xl border border-emerald-300/20 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                            <p className="font-semibold">
                              {selectedIdea.title}{" "}
                              {intakeCopy.selectedIdeaSuffix}
                            </p>
                            <p className="mt-1">
                              {replaceTemplateTokens(
                                intakeCopy.generationRunAttached,
                                {
                                  runId: generationRunId,
                                },
                              )}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-6 text-sm leading-6 text-muted-foreground">
                {intakeCopy.paymentLocked}
                {showDevPaymentBypass ? (
                  <div className="mt-4 rounded-2xl border border-amber-300/25 bg-amber-500/10 p-4 text-amber-100">
                    <p className="font-semibold">
                      {intakeCopy.devShortcutTitle}
                    </p>
                    <p className="mt-1">{intakeCopy.devShortcutBody}</p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </CustomOrderOwnerAccessGate>
      </div>
    </>
  );
};

export default CustomCaseIntakePage;
